// Live-data preview for the 'multi-tabular' capture template. Renders
// every header column as an inline editable cell in a grid laid out per
// the designer's column order (x position) + width (px). Save/Retrieve
// go through the same useEmbedRecords + livePreviewProcess pipeline as
// the list+form variant so the rows here see identical DB-side
// validation, audit columns, and resolve-defaults.

import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import {
  AlertCircle, ArrowDownAZ, ArrowUpAZ, Check, ChevronRight, Copy, Equal, FilePlus, ListFilter,
  Loader2, Pencil, Play, Plus, Redo2, RefreshCw, Save, RotateCcw, Trash2, Undo2, X,
} from 'lucide-react'

import type { CaptureRuntime } from './contract'
import type { DataAccess } from '../../dataBinding'
import {
  allSqlModels, buildParentPk, childFormsOf, columnFormKey, gridOrderedColumns,
  headerFormNo, isAutoGenerate, isKey, isMandatory, isProtect, mainSqlModel, parseApiError,
  rowEditable, visibleRowActions, listHeadingOf,
} from '../utils'
import {
  useEmbedRecords, type EmbedRecord,
} from './useEmbedRecords'
import { useWarningConfirm } from '../WarningsConfirmModal'
import { useSaveDiscardGuard } from '../SaveDiscardModal'
import { SplitScrollTable, type SplitRange, type SplitScrollHandle } from '../SplitScrollTable'
import { TxnRuntimeProvider } from '../context'
import { useItemChange, type ItemChangeExecutor } from '../list/useItemChange'
import EnforcedFilterControls, { useEnforcedFilterParts } from '../list/EnforcedFilterControls'
import ListToolbar from '../list/ListToolbar'

import type {
  ActionDef, ColumnDef, SqlModel, TransactionModel,
} from '../types'
import { formLinesViewOnly } from '../types'
import RowActionsMenu, { type RowAction } from '../list/RowActionsMenu'
import ChildTabularGrid, { type ChildCellSetter, type ChildRow } from '../list/ChildTabularGrid'
import {
  colKeyOf, layoutIsDirty, orderedDisplayCols, widthOf,
  useColumnDnd, RESIZE_HANDLE_STYLE,
  type GridLayoutApi,
} from '../list/columnLayout'
import { useGridLayout } from '../list/useGridLayout'
import { LayoutActions } from '../list/LayoutActions'
import type { EmbedColumnTotal, EmbedConfig, EmbedGridUx, PageInput } from '@twasta/page-schema'

import GridToolbar from '../list/GridToolbar'
import FilterChipBar from '../list/FilterChipBar'
import AddFilterButton from '../list/AddFilterButton'
import ColumnTotalsRow from '../list/ColumnTotalsRow'
import ErrorPanel from '../list/ErrorPanel'
import { applyGridState } from '../list/applyGridState'
import { useGridState } from '../list/useGridState'
import { useExcelGrid } from '../../grid'
import { InlineCellEditor, coerceCellText } from '../list/InlineCellEditor'
import type { GridErrorItem } from '../list/gridTypes'
import { afterRender, flushRowCompletions, listAllChildRows } from '../screen/detailsBuffer'

// Everything the expanded child grids need, threaded down through
// TabularGrid → FragmentRow → ChildTabularGrid.
interface ChildGridApi {
  updateDetailRows: (
    id: string, table: string,
    updater: (prev: Array<Record<string, unknown>>) => Array<Record<string, unknown>>,
    coalesceKey?: string,
  ) => void
  seedDetails: (id: string, table: string, rows: Array<Record<string, unknown>>) => void
  seedRowChildren: (
    id: string, childFormNo: string, childTxnId: string,
    gcFormNo: string, rows: Array<Record<string, unknown>>,
  ) => void
  loadChildRows: (formNo: string, parentPk: Record<string, unknown>) => Promise<Array<Record<string, unknown>>>
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  /** Injected item_change executor for the child grids' cell cascades. */
  execute: ItemChangeExecutor
  dataAccess?: DataAccess
  busy: boolean
  // Full transaction model, so a child grid can resolve its OWN child forms
  // (grandchildren) and render them as nested sub-grids.
  model: TransactionModel | null
  /** Merge values into a record's header (a line's row_complete may return
   *  the header's recomputed totals). */
  updateHeader: (id: string, patch: Record<string, unknown>) => void
  /** Registers a mounted child grid's cell setter (see TabularReviewHandle.setCell). */
  registerCellSetter: (recordId: string, formNo: string, setter: ChildCellSetter | null) => void
}

/** How a changed cell shows what it used to hold. */
export type ChangeTrail = 'off' | 'markers' | 'inline'

/**
 * A review screen wrapped around the grid — the Assistant's file import is the
 * one today. Everything here is optional and the plain grid ignores it; the
 * host owns the preferences (it draws the menu that sets them) and the grid
 * reports back what the host has to mirror: every record, and the selection.
 */
export interface TabularReviewOptions {
  /** How changed cells show their old value. Default 'markers'. */
  changeTrail?: ChangeTrail
  /** 'compact' tightens the rows. Default 'comfortable'. */
  density?: 'comfortable' | 'compact'
  /** Hide records identical to what is stored (errors are never hidden). */
  hideUnchanged?: boolean
  /** Open the detail rows of every new or edited record. */
  openChangedDetails?: boolean
  /** Checkbox column + bulk bar even when the object has no bulk actions. */
  selectable?: boolean
  /** Wording for dropping a record from the edit set ("Leave out of this
   *  import"). When set the drop is not confirmed — Undo brings it back. */
  removeLabel?: string
  /** Extra items at the top of each row's menu (e.g. "Show in source"). */
  rowMenuItems?: (rec: EmbedRecord) => RowAction[]
  /** Fires with EVERY record after each change (not just unsynced ones). */
  onRecordsView?: (records: EmbedRecord[]) => void
  /** Fires when the user selects a record in the grid. */
  onSelectRecord?: (rec: EmbedRecord | null) => void
  /** Drawn to the right of the rows, inside the grid frame. Supersedes the
   *  grid's own error drawer while it is shown. */
  sidePanel?: React.ReactNode
}

/** Imperative control a review host holds over the grid. */
export interface TabularReviewHandle {
  /** Select a record, open its details, and scroll it into view — clearing
   *  any status filter or search that hides it. */
  focusRecord: (id: string) => void
  /** Put every unsaved record back as it was seeded (asks first). */
  revertAll: () => void
  /** True when any record differs from its seed or has been dropped. */
  canRevert: () => boolean
  /** Write one cell exactly as if the person typed it — the item_change
   *  cascade fires. `formNo` null = the header; a child row is addressed by
   *  its `_localId` (the record is opened first so its grid is mounted).
   *  Resolves false when the cell could not be reached. */
  setCell: (recordId: string, formNo: string | null, rowId: string | null,
    field: string, value: unknown) => Promise<boolean>
  /** Open the record and put the cursor IN one cell — scrolled into view
   *  (both ways), its input focused and the cell briefly outlined. `formNo`
   *  null = the header; a child row by its `_localId` (or saved txn id).
   *  Resolves false when the cell never appeared (a hidden column). */
  focusCell: (recordId: string, formNo: string | null, rowId: string | null,
    field: string) => Promise<boolean>
}

interface Props {
  /** Injected host runtime: data adapter + form-stack transport/notifier +
   *  layout persistence + optional ensureReady (deploy). See contract.ts. */
  runtime: CaptureRuntime
  /** Bump to force a model reload — replaces the platform's object-version
   *  store subscription (the designer shim passes `useObjectVersion(...)`). */
  versionKey?: unknown
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  /** Site-mode routing for model + CRUD. */
  dataAccess?: DataAccess
  onSetInput?: (name: string, value: unknown) => void
  /**
   * Runtime interface switcher rendered on the right of the toolbar row.
   * Set when this grid is used as an alternate view of a realtime list
   * embed (EmbedListInterfaceHost); unset in the normal capture path.
   */
  viewSwitcher?: React.ReactNode
  // Extra controls for the toolbar so this grid matches the list interface:
  // `beforeAddActions` render just before "Add row" (e.g. the Approvals inbox
  // button) and `headerActions` render after "Save All" (e.g. the 3-dot
  // DeployMenu). Set by the standalone Live Preview tab; unset in embeds.
  beforeAddActions?: React.ReactNode
  headerActions?: React.ReactNode
  // Imperative control of this grid's split-scroll table (the "Freeze first
  // column" menu item drives it).
  splitHandle?: React.Ref<SplitScrollHandle>
  /**
   * Enforced-filter selection shared with the sibling List view so switching
   * to this tabular view keeps the active filter set / overrides rather than
   * loading the full table.
   */
  enforcedOverrides?: Record<string, { from?: string; to?: string; value?: string }>
  filterSetId?: string
  bypassEnforced?: boolean
  /** The list tab the screen is on — the spreadsheet lists the same records. */
  listTab?: string | null
  /** The transaction screen's own chrome: the screen's one toolbar row (scope
   *  · column filters · sort · search · layout switch) on top, then this
   *  grid's action band, then the grid — as the List reads. Off (embeds, the
   *  import review) ⇒ unchanged. */
  screenChrome?: boolean
  /**
   * Apply the user's edited enforced-filter values. Shared with the List view
   * (via the common parent) so the enforced filter stays visible + adjustable
   * here instead of vanishing when the viewer switches to multi-tabular.
   */
  onApplyOverrides?: (overrides: Record<string, { from?: string; to?: string; value?: string }>) => void
  /** Switch the active filter set, or choose "No filter (all records)". */
  onSelectFilterSet?: (sel: { setId?: string; bypass?: boolean }) => void
  // ── Custom + bulk actions (parity with the List view) ───────────────────
  // Row-scoped custom actions populate each row's 3-dot menu (run against THAT
  // record); bulk actions run against the multi-row selection. The host owns
  // execution (the confirmation gate + result/bulk-result modals) — these run
  // against a record's server txn_id, so the grid first flushes any unsaved
  // edits via a Save/Discard prompt (see useSaveDiscardGuard) and resolves each
  // target's txn_id once the records settle. Return a promise so the grid can
  // refresh after the action's server-side effect.
  rowActions?: ActionDef[]
  onRunRowAction?: (name: string, txnId: string, header?: Record<string, unknown>) => void | Promise<unknown>
  bulkActions?: ActionDef[]
  onRunBulkAction?: (name: string, txnIds: string[]) => void | Promise<unknown>
  bulkBusy?: boolean
  // Access gating (parity with the sibling List view's rights). Default true so
  // the DESIGNER — which passes nothing — stays fully enabled. A read-only
  // runtime viewer passes canAdd=false so the "Add row" / "Save All" writes it
  // would 403 on never appear. The server stays the authoritative gate.
  canAdd?: boolean
  canEdit?: boolean
  // Fired after a Save All that committed at least one record, so the host can
  // refresh whatever list it renders around this grid (restores the retired
  // RuntimeTabularGrid `onSavedAny`).
  onSaved?: () => void
  /** false ⇒ never pull saved rows from the server (mount, filter change,
   *  refresh, discard). The grid then holds ONLY the records it was seeded
   *  with through EmbedDocStateContext — the Assistant's file-import preview,
   *  which must show the file's rows, not the whole table. Default true. */
  autoRetrieve?: boolean
  /** Review-screen options (see TabularReviewOptions). */
  review?: TabularReviewOptions
  /** Imperative control for a review host (see TabularReviewHandle). */
  reviewHandle?: React.Ref<TabularReviewHandle>
}

// ── Record kinds: what Save All will do with a record ─────────────────
//
// The row tag and the filter chips speak about the RECORD, not the hook's
// lifecycle word: a failed new record is still "New" (with the fix badge), a
// retrieved one is "Unchanged" — identical to what is stored, nothing to save.
export type RecordKind = 'new' | 'edited' | 'same' | 'saved'
type StatusFilter = 'all' | RecordKind | 'fix'

export function kindOf(rec: EmbedRecord): RecordKind {
  switch (rec.status) {
    case 'unsaved': return 'new'
    case 'dirty': return 'edited'
    case 'retrieved': return 'same'
    case 'saved': return 'saved'
    default: return rec.primaryKey ? 'edited' : 'new'   // failed / partial
  }
}

/** A record that cannot be saved as it stands. */
export function needsFix(rec: EmbedRecord): boolean {
  return !!rec.recordError
    || Object.keys(rec.fieldErrors || {}).length > 0
    || rec.status === 'failed' || rec.status === 'partial'
}

const KIND_TAG: Record<RecordKind, {
  label: string; title: string; bg: string; fg: string; Icon: typeof Plus
}> = {
  new: { label: 'New', title: 'Will be added by Save All', bg: 'rgba(59,130,246,0.14)', fg: '#1d4ed8', Icon: Plus },
  edited: { label: 'Edited', title: 'Will be updated by Save All', bg: 'rgba(217,138,18,0.16)', fg: '#a15c07', Icon: Pencil },
  same: { label: 'Unchanged', title: 'Unchanged — matches what is stored, nothing to save', bg: 'rgba(100,116,139,0.14)', fg: '#526071', Icon: Equal },
  saved: { label: 'Saved', title: 'Saved', bg: 'rgba(16,185,129,0.16)', fg: '#047857', Icon: Check },
}

// Who changed a cell, for the change trail. The seed is the baseline: a
// difference between what is stored and the seed came in with the seed (the
// file); a difference between the seed and the cell now is the user's.
export type CellChangeBy = 'file' | 'you'
export const TRAIL_TONE: Record<CellChangeBy, { bg: string; mark: string; fg: string; was: string }> = {
  file: { bg: 'rgba(217,138,18,0.13)', mark: '#d98a12', fg: '#a15c07', was: 'Changed by the file — stored value' },
  you: { bg: 'rgba(124,58,237,0.10)', mark: '#7c3aed', fg: '#6d28d9', was: 'Changed by you — was' },
}

function SyncStatus({ inSync }: { inSync: boolean; total?: number }) {
  return inSync ? (
    <span style={{ ...syncPill, background: 'rgba(16,185,129,0.18)', color: '#047857' }}
      title="Local state matches the server">
      ● in sync
    </span>
  ) : (
    <span style={{ ...syncPill, background: 'rgba(234,179,8,0.18)', color: '#a16207' }}
      title="Local edits have not been pushed to the server">
      ● not synced
    </span>
  )
}

const syncPill: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '2px 8px', borderRadius: 10,
  fontSize: '0.8125rem', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
}

/** Segmented All / New / Edited / Unchanged / Saved / Fix counter — each segment
 *  filters the grid to that kind; clicking the active one clears it. */
function StatusChips({ counts, value, onChange }: {
  counts: Record<StatusFilter, number>
  value: StatusFilter
  onChange: (v: StatusFilter) => void
}) {
  const defs: Array<{ k: StatusFilter; label: string; fg: string; Icon: typeof Plus; title: string }> = [
    { k: 'all', label: 'All', fg: 'var(--color-text)', Icon: ListFilter, title: 'Every record' },
    { k: 'new', label: 'New', fg: KIND_TAG.new.fg, Icon: Plus, title: KIND_TAG.new.title },
    { k: 'edited', label: 'Edited', fg: KIND_TAG.edited.fg, Icon: Pencil, title: KIND_TAG.edited.title },
    { k: 'same', label: 'Unchanged', fg: KIND_TAG.same.fg, Icon: Equal, title: KIND_TAG.same.title },
  ]
  if (counts.saved > 0) defs.push({ k: 'saved', label: 'Saved', fg: KIND_TAG.saved.fg, Icon: Check, title: 'Saved in this session' })
  defs.push({ k: 'fix', label: 'Fix', fg: '#b42318', Icon: AlertCircle, title: 'Records that need a fix before they can be saved' })
  return (
    <div role="group" aria-label="Record status — click to filter" style={{
      display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 8,
      overflow: 'hidden', flexShrink: 0,
    }}>
      {defs.map((d, i) => {
        const on = value === d.k
        return (
          <button
            key={d.k} type="button"
            aria-pressed={on}
            title={`${d.title}${on && d.k !== 'all' ? ' — click again to show all' : ''}`}
            onClick={() => onChange(on && d.k !== 'all' ? 'all' : d.k)}
            style={{
              height: 28, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 10px',
              border: 0, borderLeft: i ? '1px solid var(--color-border)' : 0,
              background: on ? d.fg : 'var(--color-bg)',
              color: on ? 'var(--color-bg)' : d.fg,
              fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer',
            }}
          >
            {d.label}
            <span style={{ fontWeight: 700 }}>{counts[d.k]}</span>
          </button>
        )
      })}
    </div>
  )
}

/** The row's tag, plus a fix badge when it cannot be saved as it stands and a
 *  dot when the user has changed it since it was seeded. */
function StatusTag({ rec, fix, youChanged }: { rec: EmbedRecord; fix: boolean; youChanged: boolean }) {
  const t = KIND_TAG[kindOf(rec)]
  const title = rec.status === 'failed' ? 'Save failed — see the highlighted cells'
    : rec.status === 'partial' ? 'Saved in part — see the highlighted rows'
      : t.title
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span title={title} style={{
        height: 22, display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '0 7px', borderRadius: 6, background: t.bg, color: t.fg,
        fontSize: '0.8125rem', fontWeight: 600,
      }}>{t.label}</span>
      {fix && (
        <span role="img" aria-label="Needs a fix" title="Needs a fix before it can be saved" style={{
          width: 16, height: 16, flexShrink: 0, borderRadius: '50%', background: '#d92d20',
          color: '#fff', fontSize: 13, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>!</span>
      )}
      {youChanged && (
        <span role="img" aria-label="You changed this record" title="You changed this record" style={{
          width: 7, height: 7, flexShrink: 0, borderRadius: '50%', background: TRAIL_TONE.you.mark,
        }} />
      )}
    </span>
  )
}

const STATUS_COL_WIDTH = 104
const LINENO_COL_WIDTH = 36
const EXPAND_COL_WIDTH = 28
const ACTIONS_COL_WIDTH = 60
// Leading checkbox column shown when bulk actions are wired.
const SELECT_COL_WIDTH = 40
// Rows are drawn in pages of this size as the user scrolls, so a large file
// import (or a big retrieve) doesn't mount thousands of rows of live cell
// editors at once. Search, filter, sort, totals, Save All and the error list
// still work over EVERY record — only the DOM is paged.
const RENDER_PAGE = 200

export default function EmbedCaptureTabularPreview({
  runtime, versionKey,
  project, embed, inputs: _inputs, inputValues, onSetInput,
  dataAccess, viewSwitcher, beforeAddActions, headerActions, splitHandle,
  enforcedOverrides, filterSetId, bypassEnforced, listTab, screenChrome = false,
  onApplyOverrides, onSelectFilterSet,
  rowActions, onRunRowAction, bulkActions, onRunBulkAction, bulkBusy,
  canAdd = true, canEdit = true, onSaved, autoRetrieve = true,
  review, reviewHandle,
}: Props) {
  const objName = embed.obj_name
  const { api, txn } = runtime
  const notify = txn.notify
  // Host-supplied freshness key (the designer shim binds the object-version
  // store); a change re-triggers the model reload exactly as the store
  // subscription did.
  const txnVersion = versionKey

  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [modelLoading, setModelLoading] = useState(false)

  useEffect(() => {
    if (!project || !objName) {
      setModel(null)
      setModelError(null)
      return
    }
    let cancelled = false
    setModelLoading(true)
    setModelError(null)
    api.loadModel(dataAccess, project, objName)
      .then((res) => {
        if (cancelled) return
        const content = res?.content
        if (!content) {
          setModel(null)
          setModelError(`Transaction "${objName}" not yet generated`)
          return
        }
        const m = content.transaction || content
        setModel(m as TransactionModel)
      })
      .catch(() => {
        if (cancelled) return
        setModelError('Failed to load transaction')
      })
      .finally(() => { if (!cancelled) setModelLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, txnVersion, dataAccess])

  const sm = useMemo(() => (model ? mainSqlModel(model) : null), [model])
  // Direct child forms of the header, with their form_no, so the expanded
  // child grid can load real rows scoped to the parent record (same routing
  // the editor-modal child tabs use). Only the header's *direct* children are
  // top-level detail grids; deeper forms (a child's own children) nest inside
  // their parent row's sub-grid (see ChildTabularGrid recursion) rather than
  // appearing as flat siblings here.
  const details = useMemo<Array<{ formNo: string; sm: SqlModel }>>(() => {
    if (!model) return []
    return childFormsOf(model, headerFormNo(model))
  }, [model])

  // ── Live column layout (drag to resize / reorder + apply to design) ──
  // End-users on a published site can rearrange their own view, but only the
  // author (designer / in-app preview) may persist it back into the design.
  const canEditDesign = (dataAccess?.mode ?? '') !== 'site'
  // Main (header) form_no — the key its layout override lives under.
  const mainFormNo = useMemo(
    () => (model ? (allSqlModels(model)[0]?.formNo ?? '') : ''),
    [model],
  )
  const gridLayout: GridLayoutApi = useGridLayout({
    model, setModel, project, objName: objName || '', canEdit: canEditDesign,
    // 'customize' (Run view / deployed runtime) writes a per-tenant overlay;
    // 'design' (the designer) bakes into the shared core design.
    mode: runtime.layoutMode ?? 'design',
    notify, persistence: runtime.layout,
  })

  const [deployState, setDeployState] = useState<
    'idle' | 'deploying' | 'deployed' | 'error'
  >('idle')
  const [deployError, setDeployError] = useState<string | null>(null)

  // "Ignore warnings and proceed" confirm for business-logic warnings on save.
  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const records = useEmbedRecords({
    api,
    notify,
    dataAccess,
    project,
    objName: objName || '',
    inputValues,
    filters: embed.filters || [],
    args: embed.args || [],
    recordVar: embed.recordVar,
    sm,
    model: model || undefined,
    enforcedOverrides,
    filterSetId,
    bypassEnforced,
    listTab,
    confirmWarnings,
  })  // The screen chrome's scope pieces (built always — hooks — drawn only
  // with `screenChrome`).
  const screenScope = useEnforcedFilterParts({
    model: (sm || { columns: [] }) as SqlModel,
    enforcedFilters: records.enforcedFilters, filterSets: records.filterSets,
    activeFilterSetId: records.activeFilterSetId, bypassed: records.bypassed,
    busy: records.busy, notices: records.filterNotices,
    onApplyFilters: onApplyOverrides, onSelectFilterSet, project, objName: objName || '',
  })


  // Refetch when resolved filter values change — same drill-down pattern
  // EmbedCapturePreview uses.
  const filtersSig = useMemo(() => {
    const fs = embed.filters || []
    return JSON.stringify(fs.map((f) => {
      const ref = f.value
      let val: unknown
      if (ref?.source === 'page_input' && ref.name) val = inputValues[ref.name] ?? ''
      else if (ref) val = ref.value ?? ''
      let fromVal: unknown
      let toVal: unknown
      if (f.op === 'between') {
        if (f.from?.source === 'page_input' && f.from.name) fromVal = inputValues[f.from.name] ?? ''
        else if (f.from) fromVal = f.from.value ?? ''
        if (f.to?.source === 'page_input' && f.to.name) toVal = inputValues[f.to.name] ?? ''
        else if (f.to) toVal = f.to.value ?? ''
      }
      return { c: f.column, o: f.op, v: val, f: fromVal, t: toVal }
    }))
  }, [embed.filters, inputValues])

  // Refetch when the shared enforced-filter selection changes too, so this
  // tabular view stays in lockstep with the List view's set/override choice.
  const enforcedSig = useMemo(
    () => JSON.stringify({ o: enforcedOverrides || {}, s: filterSetId || '', b: !!bypassEnforced, t: listTab || '' }),
    [enforcedOverrides, filterSetId, bypassEnforced, listTab],
  )

  const initialRetrieveDoneRef = useRef(false)
  useEffect(() => {
    if (!initialRetrieveDoneRef.current) {
      initialRetrieveDoneRef.current = true
      return
    }
    if (deployState !== 'deployed') return
    // Auto-refetch on filter change ONLY when in sync. Pending edits
    // block the refetch; the out-of-sync banner instructs the user to
    // Save All or Discard changes first.
    if (records.hasPendingChanges || !autoRetrieve) return
    records.retrieve().catch(() => { /* surfaced via lastSaveError */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSig, enforcedSig, deployState])

  useEffect(() => {
    if (!project || !objName) return
    let cancelled = false
    // Site mode (public end-user page): schema already deployed by the author;
    // site users must not call the author-only deploy endpoint (it 401s).
    if (dataAccess?.mode === 'site') {
      setDeployState('deployed')
      if (autoRetrieve) void records.retrieve().catch(() => { /* non-fatal */ })
      return () => { cancelled = true }
    }
    setDeployState('deploying')
    setDeployError(null)
    // The host prepares the backing store (the designer shim deploys the
    // live-preview schema, idempotently across remounts); a host that is
    // always deployed (standalone runtime) omits ensureReady.
    ;(runtime.ensureReady
      ? runtime.ensureReady(project, objName)
      : Promise.resolve())
      .then(async () => {
        if (cancelled) return
        setDeployState('deployed')
        if (!autoRetrieve) return
        try { await records.retrieve() } catch { /* non-fatal */ }
      })
      .catch((err) => {
        if (cancelled) return
        const { message } = parseApiError(err)
        setDeployError(message)
        setDeployState('error')
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, dataAccess])

  // Selection plumbing — last-clicked row drives selectedIdVar /
  // selectedDataVar like the list+form variant does.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const lastSelectionRef = useRef<{ id: string | null; data: unknown }>({
    id: null, data: null,
  })
  const writeSelectionVars = useCallback((rec: EmbedRecord | null) => {
    if (!onSetInput) return
    const pk = rec?.primaryKey ?? null
    const last = lastSelectionRef.current
    if (embed.selectedIdVar && last.id !== pk) {
      onSetInput(embed.selectedIdVar, pk ?? '')
    }
    if (embed.selectedDataVar && last.data !== rec) {
      onSetInput(embed.selectedDataVar, rec ? rec.header : undefined)
    }
    lastSelectionRef.current = { id: pk, data: rec }
  }, [embed.selectedIdVar, embed.selectedDataVar, onSetInput])

  // The review host's callbacks, read through a ref so a host re-render with a
  // fresh options object doesn't re-create every handler below.
  const reviewRef = useRef(review)
  reviewRef.current = review

  const handleSelect = useCallback((rec: EmbedRecord) => {
    setSelectedId(rec.id)
    writeSelectionVars(rec)
    reviewRef.current?.onSelectRecord?.(rec)
  }, [writeSelectionVars])

  // Restore selection from `inputValues[embed.selectedIdVar]` when the
  // var carries a value the embed didn't write itself — typically a
  // smart document being reopened where saved vars rehydrate before
  // the embed's records load. Match against primaryKey since that's
  // what the var stores. Seeds lastSelectionRef so writeSelectionVars
  // treats the echo as a no-op.
  useEffect(() => {
    const varName = embed.selectedIdVar
    if (!varName) return
    const raw = inputValues[varName]
    const targetPk = raw == null || raw === '' ? null : String(raw)
    const currentRec = selectedId ? records.records.find((r) => r.id === selectedId) || null : null
    const currentPk = currentRec?.primaryKey ?? null
    if (targetPk === currentPk) return
    if (targetPk == null) {
      setSelectedId(null)
      lastSelectionRef.current = { id: null, data: null }
      return
    }
    const rec = records.records.find((r) => r.primaryKey === targetPk)
    if (!rec) return
    setSelectedId(rec.id)
    lastSelectionRef.current = { id: targetPk, data: rec }
  }, [embed.selectedIdVar, inputValues, records.records, selectedId])

  // Which records have their detail rows open. An explicit choice (chevron,
  // expand-all, focus) wins; otherwise `openChangedDetails` opens every new or
  // edited record. Several can be open at once.
  const [expandedMap, setExpandedMap] = useState<ReadonlyMap<string, boolean>>(() => new Map())
  // The record whose chevron was just clicked — only it scrolls itself into
  // view, so expand-all doesn't drag the viewport through every row.
  const [scrollOnExpandId, setScrollOnExpandId] = useState<string | null>(null)
  const openChanged = !!review?.openChangedDetails
  const isExpandedRec = useCallback((rec: EmbedRecord): boolean => {
    const v = expandedMap.get(rec.id)
    if (v !== undefined) return v
    return openChanged && (rec.status === 'unsaved' || rec.status === 'dirty')
  }, [expandedMap, openChanged])
  const toggleExpand = useCallback((rec: EmbedRecord) => {
    const open = !isExpandedRec(rec)
    setExpandedMap(prev => new Map(prev).set(rec.id, open))
    setScrollOnExpandId(open ? rec.id : null)
  }, [isExpandedRec])
  const collapseAll = useCallback(() => {
    setExpandedMap(new Map())
    setScrollOnExpandId(null)
  }, [])

  const handleAdd = useCallback(async () => {
    try {
      const rec = await records.addNew()
      setSelectedId(rec.id)
    } catch (err) {
      const { message } = parseApiError(err)
      notify.error(`Could not add new record: ${message}`)
    }
  }, [records, notify])

  // The live records hook — saveBatch reads the records it was created with,
  // so a save that waits for row completions must call the one rendered AFTER.
  const recordsRef = useRef(records)
  recordsRef.current = records

  // Save every pending record, first finishing any inline line still waiting
  // for its row_complete. When one ran, its rebuilt rows land on the next
  // render, so the save goes through the hook as it is then. A refused line
  // stops the save (its message is on the row).
  const saveAllRecords = useCallback(async (): Promise<{ success: boolean; failedCount: number }> => {
    try {
      if (await flushRowCompletions()) await afterRender()
    } catch (err) {
      notify.error(err instanceof Error ? err.message : String(err))
      return { success: false, failedCount: 0 }
    }
    return recordsRef.current.saveBatch()
  }, [notify])

  const handleSaveAll = useCallback(async (): Promise<void> => {
    const outcome = await saveAllRecords()
    if (outcome.success) {
      notify.success('All records saved')
      onSaved?.()
    } else if (outcome.failedCount > 0) {
      notify.error(`${outcome.failedCount} record${outcome.failedCount === 1 ? '' : 's'} failed validation`)
    }
  }, [saveAllRecords, notify, onSaved])

  const handleRetrieve = useCallback(async () => {
    if (records.hasPendingChanges || !autoRetrieve) return
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      notify.error(`Refresh failed: ${message}`)
    }
  }, [records, notify, autoRetrieve])

  const handleDiscardChanges = useCallback(async () => {
    const pending = records.counts.unsaved + records.counts.dirty +
      records.counts.failed + records.counts.partial
    // A grid that never retrieves holds ONLY what it was seeded with (the
    // file import). Dropping the unsaved rows there would lose the file's
    // records with nothing to reload them, so it rewinds to the seed instead.
    if (!autoRetrieve) {
      if (!window.confirm(
        'Undo all your edits and put every unsaved row back the way it was when this opened?',
      )) return
      collapseAll()
      if (!records.restoreSeed()) {
        notify.error('Nothing to restore — this grid was not opened with seeded rows.')
      }
      return
    }
    if (!window.confirm(
      `Discard ${pending} pending change${pending === 1 ? '' : 's'} and reload from the server?`,
    )) return
    collapseAll()
    records.discardLocalEdits()
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      notify.error(`Refresh failed: ${message}`)
    }
  }, [records, notify, autoRetrieve, collapseAll])

  // Best-effort item_change cascade. Hook swallows network/server
  // failures so the user can keep editing even when the deployed
  // function is missing or the backend is down.
  const triggerItemChange = useItemChange<{ recordId: string }>({
    execute: api.execute,
    dataAccess,
    project, objName: objName || '', sm,
    onUpdates: (ctx, updates) => {
      records.updateHeader(ctx.recordId, updates)
    },
  })

  // Latest records for the debounced item_change, which reads the row when
  // the call goes out: a paste writes several cells of one row in a single
  // tick, and the cascade for the first must see the values of the others.
  const latestRecordsRef = useRef(records.records)
  latestRecordsRef.current = records.records

  const onCellChange = useCallback((id: string, dbName: string, v: unknown) => {
    const rec = records.records.find(r => r.id === id)
    const newHeader = { ...(rec?.header || {}), [dbName]: v }
    records.updateHeader(id, { [dbName]: v })
    triggerItemChange({ recordId: id }, dbName, () => (
      latestRecordsRef.current.find(r => r.id === id)?.header || newHeader
    ))
    // Editing a cell implicitly selects the row — write the selection
    // variables so chained embeds / bindings react to whichever row
    // the user is currently editing, not just rows the user explicitly
    // clicks. Without this, keyboard-only navigation (or any cell
    // change before the user clicks the row) never binds the vars.
    if (rec && selectedId !== id) {
      setSelectedId(id)
      reviewRef.current?.onSelectRecord?.(rec)
    }
    if (rec) {
      writeSelectionVars({ ...rec, header: newHeader })
    }
  }, [records, triggerItemChange, selectedId, writeSelectionVars])

  // Drop records from the local edit set (never a server delete). A review
  // host that names the action (`removeLabel`) skips the confirm — Undo brings
  // the records back — and one call over many ids is ONE undo step: the hook's
  // checkpoint sees the same pre-edit state for the whole synchronous loop.
  const dropRecords = useCallback((ids: string[]) => {
    if (ids.length === 0) return
    const gone = new Set(ids)
    if (selectedId && gone.has(selectedId)) {
      setSelectedId(null)
      writeSelectionVars(null)
      reviewRef.current?.onSelectRecord?.(null)
    }
    setExpandedMap(prev => {
      if (![...gone].some(id => prev.has(id))) return prev
      const next = new Map(prev)
      gone.forEach(id => next.delete(id))
      return next
    })
    ids.forEach(id => records.removeLocal(id))
  }, [records, selectedId, writeSelectionVars])

  const handleRemove = useCallback((id: string) => {
    const rec = records.records.find(r => r.id === id)
    if (!rec) return
    if (!review?.removeLabel) {
      const msg = rec.status === 'unsaved'
        ? 'Remove this unsaved record?'
        : `This will drop "${rec.primaryKey || ''}" from the local edit set (the server copy stays). Continue?`
      if (!window.confirm(msg)) return
    }
    dropRecords([id])
  }, [records.records, review?.removeLabel, dropRecords])

  // ── Grid UX (search / filter / sort / fullscreen / totals) ──
  const gridUx: EmbedGridUx = embed.gridUx || {}
  const grid = useGridState()
  const cols = useMemo(() => (sm ? gridOrderedColumns(sm) : []), [sm])

  const filteredRecords = useMemo(() => {
    if (!grid.search && grid.filters.length === 0 && grid.sort.length === 0) {
      return records.records
    }
    // Decorate records so the column-keyed filters/sort can address the
    // header values directly. Carry __id back to recover the EmbedRecord.
    const decorated = records.records.map(r => ({
      ...(r.header || {}),
      __id: r.id,
    }))
    const visible = applyGridState(
      decorated, grid.search, grid.filters, grid.sort,
    )
    const byId = new Map(records.records.map(r => [r.id, r]))
    return visible
      .map(v => byId.get(String(v.__id)))
      .filter((r): r is EmbedRecord => !!r)
  }, [records.records, grid.search, grid.filters, grid.sort])

  // ── Change filter (the All / New / Edited / Unchanged / Fix chips) ──
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  // The record a review host last focused stays on screen under "hide
  // unchanged", so jumping to it from the source never lands on nothing.
  const [pinnedId, setPinnedId] = useState<string | null>(null)
  const hideUnchanged = !!review?.hideUnchanged
  const statusCounts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: 0, new: 0, edited: 0, same: 0, saved: 0, fix: 0 }
    for (const r of records.records) {
      c.all += 1
      c[kindOf(r)] += 1
      if (needsFix(r)) c.fix += 1
    }
    return c
  }, [records.records])
  const visibleRecords = useMemo(() => {
    if (statusFilter === 'all' && !hideUnchanged) return filteredRecords
    return filteredRecords.filter((r) => {
      if (statusFilter === 'fix') {
        if (!needsFix(r)) return false
      } else if (statusFilter !== 'all' && kindOf(r) !== statusFilter) {
        return false
      }
      if (hideUnchanged && kindOf(r) === 'same' && !needsFix(r) && r.id !== pinnedId) return false
      return true
    })
  }, [filteredRecords, statusFilter, hideUnchanged, pinnedId])

  // Has the user changed this record since it was seeded (the purple dot)?
  // Memoised per record object: records are immutable, so a WeakMap entry can
  // never go stale.
  const seeded = records.seeded
  const youCacheRef = useRef(new WeakMap<EmbedRecord, boolean>())
  const youChanged = useCallback((rec: EmbedRecord): boolean => {
    const seed = seeded?.get(rec.id)
    if (!seed || rec.status === 'saved') return false
    const hit = youCacheRef.current.get(rec)
    if (hit !== undefined) return hit
    let changed = rowValuesDiffer(seed.header, rec.header)
    if (!changed) {
      const forms = new Set([...Object.keys(seed.details || {}), ...Object.keys(rec.details || {})])
      for (const f of forms) {
        if (detailRowsChanged(
          seed.details?.[f] as ChildRow[] | undefined, rec.details?.[f] as ChildRow[] | undefined,
        )) { changed = true; break }
      }
    }
    youCacheRef.current.set(rec, changed)
    return changed
  }, [seeded])

  // Tell a review host about every change to the record set — it mirrors
  // them in its source pane (dropped records struck through) and fix list.
  useEffect(() => {
    try { reviewRef.current?.onRecordsView?.(records.records) }
    catch (err) { console.warn('[EmbedCaptureTabularPreview] review host rejected the records update', err) }
  }, [records.records])

  // Scroll a record into view once the render that shows it has landed (two
  // frames: one for React's commit, one for the split panes' row pairing).
  const viewportNodeRef = useRef<HTMLDivElement | null>(null)
  const [scrollTo, setScrollTo] = useState<{ id: string; n: number } | null>(null)
  useEffect(() => {
    if (!scrollTo) return
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        try {
          const sel = `[data-split-row="${scrollTo.id.replace(/(["\\])/g, '\\$1')}"]`
          const rows = viewportNodeRef.current?.querySelectorAll<HTMLElement>(sel)
          // The last match is the trailing (scrolling) pane's row; the
          // leading pane follows it through the split table's scroll sync.
          rows?.[rows.length - 1]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        } catch (err) {
          console.warn('[EmbedCaptureTabularPreview] could not scroll to the record', err)
        }
      })
    })
    return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner) }
  }, [scrollTo])

  const focusRecord = useCallback((id: string) => {
    const rec = records.records.find(r => r.id === id)
    if (!rec) return
    // Clear whatever hides it: the status chip first, then search / filters.
    if (!visibleRecords.some(r => r.id === id)) {
      setStatusFilter('all')
      if (!filteredRecords.some(r => r.id === id)) {
        grid.setSearch('')
        grid.clearFilters()
      }
    }
    setPinnedId(id)
    setSelectedId(id)
    writeSelectionVars(rec)
    if (details.length > 0) setExpandedMap(prev => new Map(prev).set(id, true))
    setScrollOnExpandId(null)
    setScrollTo({ id, n: Date.now() })
  }, [records.records, visibleRecords, filteredRecords, grid, writeSelectionVars, details.length])

  // Mounted child grids' cell setters, by `${recordId}|${formNo}`.
  const cellSettersRef = useRef<Map<string, ChildCellSetter>>(new Map())
  const registerCellSetter = useCallback(
    (recordId: string, formNo: string, setter: ChildCellSetter | null) => {
      const key = `${recordId}|${formNo}`
      if (setter) cellSettersRef.current.set(key, setter)
      else cellSettersRef.current.delete(key)
    }, [])

  const setCell = useCallback(async (
    recordId: string, formNo: string | null, rowId: string | null, field: string, value: unknown,
  ): Promise<boolean> => {
    if (!records.records.some(r => r.id === recordId)) return false
    if (!formNo) {
      onCellChange(recordId, field, value)
      return true
    }
    if (!rowId) return false
    const key = `${recordId}|${formNo}`
    if (!cellSettersRef.current.has(key)) {
      // The record's child grids mount when it is opened; wait for them.
      focusRecord(recordId)
      for (let i = 0; i < 40 && !cellSettersRef.current.has(key); i++) {
        await new Promise<void>((done) => window.setTimeout(done, 25))
      }
    }
    const setter = cellSettersRef.current.get(key)
    if (!setter) {
      console.warn('[EmbedCaptureTabularPreview] child grid not mounted for', key)
      return false
    }
    return setter(rowId, field, value)
  }, [records.records, onCellChange, focusRecord])

  const focusCell = useCallback(async (
    recordId: string, formNo: string | null, rowId: string | null, field: string,
  ): Promise<boolean> => {
    if (!records.records.some(r => r.id === recordId)) return false
    focusRecord(recordId)
    const esc = (v: string) => v.replace(/(["\\])/g, '\\$1')
    const selectors = !formNo
      ? [`[data-grid-cell="${esc(`${recordId}::${field}`)}"]`]
      : rowId
        ? [`l:${rowId}`, `t:${rowId}`, rowId].map(k =>
          `[data-grid-row="${esc(k)}"][data-grid-col="${esc(field)}"]`)
        : []
    if (!selectors.length) return false
    // The record's row (and, for a line, its child grid) lands a few frames
    // after the focus; wait for the cell rather than guessing a delay.
    let cell: HTMLElement | null = null
    for (let i = 0; i < 60 && !cell; i++) {
      await new Promise<void>((done) => window.setTimeout(done, 25))
      for (const sel of selectors) {
        const all = viewportNodeRef.current?.querySelectorAll<HTMLElement>(sel)
        if (all && all.length) { cell = all[all.length - 1]; break }
      }
    }
    if (!cell) {
      console.warn('[EmbedCaptureTabularPreview] cell not on screen for', recordId, formNo, rowId, field)
      return false
    }
    try {
      cell.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
      const input = cell.querySelector<HTMLElement>('input, select, textarea, [contenteditable="true"], button')
      input?.focus({ preventScroll: true })
      const prev = cell.style.outline
      const prevOffset = cell.style.outlineOffset
      cell.style.outline = '2px solid #d92d20'
      cell.style.outlineOffset = '-2px'
      window.setTimeout(() => { cell!.style.outline = prev; cell!.style.outlineOffset = prevOffset }, 1600)
    } catch (err) {
      console.warn('[EmbedCaptureTabularPreview] could not focus the cell', err)
    }
    return true
  }, [records.records, focusRecord])

  const canRevert = useMemo(() => {
    if (!seeded) return records.hasPendingChanges
    if (records.records.length !== seeded.size) return true
    return records.records.some(r => !seeded.has(r.id) || youChanged(r))
  }, [seeded, records.records, records.hasPendingChanges, youChanged])

  useImperativeHandle(reviewHandle, () => ({
    focusRecord,
    revertAll: () => { void handleDiscardChanges() },
    canRevert: () => canRevert,
    setCell,
    focusCell,
  }), [focusRecord, handleDiscardChanges, canRevert, setCell, focusCell])

  // Review-host presentation choices (the plain grid keeps its defaults).
  const trail: ChangeTrail = review?.changeTrail ?? 'markers'
  const dense = review?.density === 'compact'
  const hostPanel = review?.sidePanel || null

  const anyExpanded = useMemo(
    () => details.length > 0 && visibleRecords.some(isExpandedRec),
    [details.length, visibleRecords, isExpandedRec],
  )
  const setAllExpanded = useCallback((open: boolean) => {
    setExpandedMap(prev => {
      const next = new Map(prev)
      visibleRecords.forEach(r => next.set(r.id, open))
      return next
    })
    setScrollOnExpandId(null)
  }, [visibleRecords])

  // Duplicate a record as a NEW one below the rest. Only what a user could
  // have typed is copied — never the key (the copy is a different record), nor
  // protected, auto-generated or audit values the server sets. Detail rows come
  // along as new rows.
  const handleDuplicate = useCallback(async (id: string) => {
    const src = records.records.find(r => r.id === id)
    if (!src) return
    const seed: Record<string, unknown> = {}
    for (const c of cols) {
      if (isKey(c) || isProtect(c) || isAutoGenerate(c)) continue
      const k = columnFormKey(c)
      if (src.header[k] !== undefined) seed[k] = src.header[k]
    }
    try {
      const rec = await records.addNew(seed)
      for (const [formNo, rows] of Object.entries(src.details || {})) {
        const copy = copyDetailRows((rows || []) as ChildRow[])
        if (copy.length) records.updateDetails(rec.id, formNo, copy)
      }
      setSelectedId(rec.id)
      writeSelectionVars(rec)
      reviewRef.current?.onSelectRecord?.(rec)
      setScrollTo({ id: rec.id, n: Date.now() })
    } catch (err) {
      const { message } = parseApiError(err)
      notify.error(`Could not duplicate the row: ${message}`)
    }
  }, [records, cols, writeSelectionVars, notify])

  // ── Bulk selection + custom/bulk actions (parity with the List view) ─────
  // 3-dot custom actions appear whenever the object declares row actions; the
  // checkbox column + bulk bar appear when there are bulk-eligible actions.
  const hasRowActions = (rowActions?.length || 0) > 0
  const bulkEnabled = (bulkActions?.length || 0) > 0
  // The checkbox column shows for bulk actions, or when a review host asks
  // for it (bulk "leave out" / "open details" need no object action).
  const selectEnabled = bulkEnabled || !!review?.selectable
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(() => new Set())
  const [anchorId, setAnchorId] = useState<string | null>(null)
  const { guard, saveDiscardModal } = useSaveDiscardGuard()
  const [pendingAction, setPendingAction] = useState<
    { kind: 'row' | 'bulk'; name: string; targets: Array<{ id: string; pk?: string }> } | null
  >(null)

  // Drop selected ids no longer present after the records change.
  useEffect(() => {
    if (!selectEnabled) return
    setSelectedRowIds(prev => {
      if (prev.size === 0) return prev
      const present = new Set(records.records.map(r => r.id))
      let changed = false
      const next = new Set<string>()
      prev.forEach(id => { if (present.has(id)) next.add(id); else changed = true })
      return changed ? next : prev
    })
  }, [records.records, selectEnabled])

  const toggleRowSelected = useCallback((id: string) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])
  const handleRowCheckboxClick = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (e.shiftKey && anchorId) {
      const a = visibleRecords.findIndex(r => r.id === anchorId)
      const b = visibleRecords.findIndex(r => r.id === id)
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a <= b ? [a, b] : [b, a]
        setSelectedRowIds(prev => {
          const next = new Set(prev)
          for (let k = lo; k <= hi; k++) next.add(visibleRecords[k].id)
          return next
        })
        setAnchorId(id)
        return
      }
    }
    toggleRowSelected(id)
    setAnchorId(id)
  }, [anchorId, visibleRecords, toggleRowSelected])
  const allSelected = selectEnabled && visibleRecords.length > 0
    && visibleRecords.every(r => selectedRowIds.has(r.id))
  const toggleSelectAll = useCallback(() => {
    setSelectedRowIds(prev => {
      const next = new Set(prev)
      const every = visibleRecords.length > 0 && visibleRecords.every(r => next.has(r.id))
      if (every) visibleRecords.forEach(r => next.delete(r.id))
      else visibleRecords.forEach(r => next.add(r.id))
      return next
    })
  }, [visibleRecords])
  const clearSelection = useCallback(() => { setSelectedRowIds(new Set()); setAnchorId(null) }, [])

  // Queue an action: flush pending edits via the Save/Discard gate (it runs on
  // PERSISTED records), then fire it against the targets once the records
  // settle (see the effect below).
  const startTabularAction = useCallback(async (
    kind: 'row' | 'bulk', name: string, targets: Array<{ id: string; pk?: string }>,
  ) => {
    if (targets.length === 0) return
    if (records.hasPendingChanges) {
      const pending = records.counts.unsaved + records.counts.dirty
        + records.counts.failed + records.counts.partial
      const choice = await guard({ actionLabel: name, pendingCount: pending })
      if (choice === 'cancel') return
      if (choice === 'save') {
        const outcome = await saveAllRecords()
        if (!outcome.success) {
          notify.error('Save the highlighted rows before running this action.')
          return
        }
      } else {
        collapseAll()
        records.discardLocalEdits()
        try { await records.retrieve() } catch { /* surfaced via toast elsewhere */ }
      }
    }
    setPendingAction({ kind, name, targets })
  }, [records, guard, notify, collapseAll, saveAllRecords])

  const runRowActionGuarded = useCallback((name: string, rec: EmbedRecord) => {
    void startTabularAction('row', name, [{ id: rec.id, pk: rec.primaryKey }])
  }, [startTabularAction])
  const runBulkActionGuarded = useCallback((name: string) => {
    const targets = records.records
      .filter(r => selectedRowIds.has(r.id))
      .map(r => ({ id: r.id, pk: r.primaryKey }))
    void startTabularAction('bulk', name, targets)
  }, [records.records, selectedRowIds, startTabularAction])

  // Once the records have settled, resolve each target's server txn_id and run
  // the queued action. A target resolves to its live record's primaryKey (a
  // just-saved new row keeps its client id across saveBatch) or its captured pk
  // (discard reloads with fresh client ids, but a saved row keeps its server
  // key). A discarded brand-new row resolves to nothing and is dropped.
  useEffect(() => {
    if (!pendingAction || records.hasPendingChanges) return
    const { kind, name, targets } = pendingAction
    const byId = new Map(records.records.map(r => [r.id, r]))
    const pks = Array.from(new Set(
      targets.map(t => byId.get(t.id)?.primaryKey ?? t.pk)
        .filter((p): p is string => !!p),
    ))
    setPendingAction(null)
    if (pks.length === 0) {
      notify.error('No saved records to run the action on.')
      return
    }
    void (async () => {
      try {
        if (kind === 'row') {
          const rec = records.records.find(r => r.primaryKey === pks[0])
          await onRunRowAction?.(name, pks[0], rec?.header)
        } else {
          await onRunBulkAction?.(name, pks)
        }
      } finally {
        if (!records.hasPendingChanges) {
          try { await records.retrieve() } catch { /* ignore */ }
        }
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction, records.hasPendingChanges])

  // Surface every field + record error for the side panel.
  const errorItems: GridErrorItem[] = useMemo(() => {
    const items: GridErrorItem[] = []
    records.records.forEach((rec, idx) => {
      const rowLabel = rec.primaryKey
        ? String(rec.primaryKey)
        : `New record #${idx + 1}`
      if (rec.recordError) {
        items.push({
          rowKey: rec.id, rowLabel,
          column: null, columnLabel: null,
          message: rec.recordError,
        })
      }
      for (const [field, msg] of Object.entries(rec.fieldErrors || {})) {
        const c = cols.find(c => columnFormKey(c) === field)
        items.push({
          rowKey: rec.id,
          rowLabel,
          column: field,
          columnLabel: (c ? listHeadingOf(c) : '') || field,
          message: msg,
        })
      }
    })
    return items
  }, [records.records, cols])

  const [errorPanelOpen, setErrorPanelOpen] = useState(false)
  // Auto-open the error panel whenever a save produces validation errors.
  // We watch the count: any non-zero count plus an active lastSaveError
  // means the user just tried to save and something is wrong.
  const errorCount = errorItems.length
  useEffect(() => {
    if (errorCount > 0 && records.lastSaveError) setErrorPanelOpen(true)
    if (errorCount === 0) setErrorPanelOpen(false)
  }, [errorCount, records.lastSaveError])

  const handleErrorSelect = useCallback((item: GridErrorItem) => {
    const rec = records.records.find(r => r.id === item.rowKey)
    if (rec) {
      setSelectedId(rec.id)
      writeSelectionVars(rec)
    }
    if (item.column) {
      // Defer so React commits the selection highlight before we steal
      // focus into the cell.
      requestAnimationFrame(() => {
        const escaped = `${item.rowKey}::${item.column}`.replace(/(["\\])/g, '\\$1')
        const cell = document.querySelector<HTMLElement>(
          `[data-grid-cell="${escaped}"]`,
        )
        if (!cell) return
        cell.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const input = cell.querySelector<HTMLElement>('input, select, textarea, button')
        if (input) input.focus()
      })
    }
  }, [records.records, writeSelectionVars])

  // Excel-like layer (cell nav + copy/paste/fill/range-select) on top of the
  // existing cell editors. Writes route through onCellChange so item_change +
  // validation still run; reads/row-growth use the same useEmbedRecords APIs.
  const writeExcelCell = useCallback((rowId: string, dbName: string, text: string) => {
    const col = cols.find((c) => columnFormKey(c) === dbName)
    if (!col) return
    const rec = records.records.find((r) => r.id === rowId)
    if (!rec) return
    // Honour the same disabled rule as InlineCellEditor — don't let paste/fill
    // overwrite protected, read-only-key, or locked (view-only) cells.
    if (!rowEditable(rec.header) || isProtect(col) || (!!rec.primaryKey && isKey(col))) return
    onCellChange(rowId, dbName, coerceCellText(col, text))
  }, [cols, records.records, onCellChange])

  const readExcelCell = useCallback((rowId: string, dbName: string): string => {
    const rec = records.records.find((r) => r.id === rowId)
    const v = rec?.header[dbName]
    return v == null ? '' : String(v)
  }, [records.records])

  // How many rows TabularGrid draws (see RENDER_PAGE). Paste / fill read the
  // target rows from the DOM, so they raise this before asking for rows.
  const [renderLimit, setRenderLimit] = useState(RENDER_PAGE)
  const renderAtLeast = useCallback((n: number) => {
    setRenderLimit((l) => (n > l ? n : l))
  }, [])

  const ensureExcelRows = useCallback(async (count: number) => {
    renderAtLeast(count)
    let have = records.records.length
    while (have < count) {
      // eslint-disable-next-line no-await-in-loop
      await records.addNew()
      have += 1
    }
  }, [records, renderAtLeast])

  const attachGrid = useExcelGrid({
    enabled: gridUx.keyboardNav !== false,
    basicNav: true,
    writeCell: writeExcelCell,
    readCell: readExcelCell,
    ensureRowCount: ensureExcelRows,
  })

  // Excel-style undo / redo over the whole grid: Ctrl/Cmd+Z undoes the
  // last edit (cell change, paste, fill, add/remove row), Ctrl/Cmd+Shift+Z
  // or Ctrl/Cmd+Y redoes. We preventDefault so the browser's native
  // input-undo doesn't fight the grid history while a cell is focused.
  const { undo, redo } = records
  const onGridKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey)) return
    const k = e.key.toLowerCase()
    if (k === 'z' && !e.shiftKey) {
      e.preventDefault()
      undo()
    } else if (k === 'y' || (k === 'z' && e.shiftKey)) {
      e.preventDefault()
      redo()
    }
  }, [undo, redo])

  // Visible width of the grid's horizontal scroll viewport. The expanded
  // child-form panel pins itself to this width (position:sticky) so its
  // Add/Refresh toolbar stays on-screen instead of stretching to the full
  // (wide) parent-table width and forcing a long horizontal scroll.
  // A callback ref + ResizeObserver tracks it (and re-attaches if the grid
  // mounts late / re-renders), updating on container resize + full-screen.
  const [viewportWidth, setViewportWidth] = useState(0)
  const scrollRoRef = useRef<ResizeObserver | null>(null)
  const scrollViewportRef = useCallback((node: HTMLDivElement | null) => {
    scrollRoRef.current?.disconnect()
    scrollRoRef.current = null
    if (!node) return
    const update = () => setViewportWidth(node.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(node)
    scrollRoRef.current = ro
  }, [])

  // Fetch existing child rows for one detail form scoped to a parent record —
  // used to lazy-load a parent's children when its row is expanded.
  const loadChildRows = useCallback(async (
    formNo: string, parentPk: Record<string, unknown>,
  ): Promise<Array<Record<string, unknown>>> => {
    return listAllChildRows(
      (q) => api.list(dataAccess, project, objName || '', q.limit, q.offset, q.formNo, q.parentPk),
      formNo, parentPk,
    )
  }, [api, dataAccess, project, objName])

  const childUploader = useMemo(
    () => api.makeUploader(dataAccess, project),
    [api, dataAccess, project],
  )

  // Bundle of everything the expanded child grids need, threaded down through
  // TabularGrid → FragmentRow.
  const childGrid = useMemo(() => ({
    updateDetailRows: records.updateDetailRows,
    seedDetails: records.seedDetails,
    seedRowChildren: records.seedRowChildren,
    loadChildRows,
    uploadDocument: childUploader,
    execute: api.execute,
    dataAccess,
    busy: records.busy || deployState !== 'deployed',
    model,
    updateHeader: records.updateHeader,
    registerCellSetter,
  }), [registerCellSetter, records.updateHeader, records.updateDetailRows, records.seedDetails, records.seedRowChildren, loadChildRows, childUploader, api.execute, dataAccess, records.busy, deployState, model])

  if (!objName) {
    return (
      <div style={emptyStyle}>
        <AlertCircle size={18} />
        <span>Pick a transaction in the property panel to embed.</span>
      </div>
    )
  }
  if (modelLoading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading transaction model…</span>
      </div>
    )
  }
  if (modelError) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{modelError}</span>
      </div>
    )
  }
  if (!sm) return null


  const columnTotals = embed.columnTotals || {}
  const hasAnyTotal = Object.values(columnTotals).some(Boolean)
  const showTotals = gridUx.totals !== false && hasAnyTotal

  // Container styles — when full-screen, anchor to viewport so the
  // toolbar, grid, totals row, and error panel all live in the same
  // overlay. Otherwise plain in-flow layout.
  const fullScreenStyle: React.CSSProperties = grid.isFullScreen ? {
    // Above the Run view's sticky top bar (zIndex 9000) so the fullscreen
    // grid's toolbar isn't hidden behind it — matches ListView's overlay.
    position: 'fixed', inset: 0, zIndex: 9410,
    background: 'var(--color-bg)',
    padding: 12,
    display: 'flex', flexDirection: 'column', gap: 8,
    overflow: 'hidden',
  } : screenChrome ? {
    display: 'flex', flexDirection: 'column', gap: 0, position: 'relative',
    height: '100%', minHeight: 0, background: 'var(--color-bg-secondary, #ffffff)',
  } : {
    display: 'flex', flexDirection: 'column', gap: 8,
    position: 'relative',
    // Fill the height the host gives us so the grid is responsive (no dead
    // space below the table). When the host is content-sized instead (e.g. a
    // smart-page embed with no fixed height) this resolves to auto and the
    // grid simply grows to its natural height.
    height: '100%', minHeight: 0,
  }

  return (
    // The core cell editors / child grids / lookup modals read their
    // transport from the TxnRuntime context — provide the host's, exactly
    // as the platform's per-component wrappers used to.
    <TxnRuntimeProvider value={txn}>
    <div style={fullScreenStyle}>
      {screenChrome && (
        <>
          <ListToolbar
            scope={screenScope.scope}
            chips={screenScope.chips}
            columns={cols}
            columnKeyOf={columnFormKey}
            {...(gridUx.filter !== false ? {
              filters: grid.filters, onUpsertFilter: grid.upsertFilter,
              onRemoveFilter: grid.removeFilter, onClearFilters: grid.clearFilters,
            } : {})}
            {...(gridUx.sort !== false ? { sort: grid.sort, onClearSort: grid.clearSort } : {})}
            search={grid.search}
            onSearch={grid.setSearch}
            trailing={viewSwitcher}
          />
          {screenScope.below}
        </>
      )}
      <div style={screenChrome ? {
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
        padding: '10px 20px', background: 'var(--color-surface-2, #f0f3f7)',
        borderBottom: '1px solid var(--color-border, #dde3ea)',
      } : {
        display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginRight: 'auto' }}>
          <StatusChips counts={statusCounts} value={statusFilter} onChange={setStatusFilter} />
          <SyncStatus
            inSync={!records.hasPendingChanges}
            total={records.counts.retrieved + records.counts.saved}
          />
          {deployState === 'deploying' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              deploying…
            </span>
          )}
          {deployState === 'error' && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-error, #b91c1c)' }}>
              deploy failed: {deployError}
            </span>
          )}
        </div>
        {errorCount > 0 && !hostPanel && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setErrorPanelOpen(true)}
            style={{ color: '#b91c1c' }}
            title="Show all validation errors"
          >
            <AlertCircle size={14} /> {errorCount} error{errorCount === 1 ? '' : 's'}
          </button>
        )}
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={() => records.undo()}
          disabled={!records.canUndo}
          title="Undo (Ctrl/Cmd+Z)"
          style={{ padding: '4px 8px' }}
        ><Undo2 size={14} /></button>
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={() => records.redo()}
          disabled={!records.canRedo}
          title="Redo (Ctrl/Cmd+Shift+Z)"
          style={{ padding: '4px 8px' }}
        ><Redo2 size={14} /></button>
        <LayoutActions
          show={canEditDesign && layoutIsDirty(gridLayout.layouts[mainFormNo])}
          applying={gridLayout.applyingFormNo === mainFormNo}
          onReset={() => gridLayout.reset(mainFormNo)}
          onApply={() => gridLayout.apply(mainFormNo)}
          applyLabel={gridLayout.applyLabel}
          applyTitle={gridLayout.applyTitle}
        />
        {/* A grid that never retrieves has nothing to refresh FROM — hide the
            button rather than show one that silently does nothing. */}
        {autoRetrieve && (
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={handleRetrieve}
          disabled={records.busy || deployState !== 'deployed' || records.hasPendingChanges}
          title={
            records.hasPendingChanges
              ? 'Local changes are not synced — Save All or Discard them before refreshing'
              : 'Reload all rows from the server'
          }
        ><RefreshCw size={14} /> Refresh</button>
        )}
        {records.hasPendingChanges && (autoRetrieve ? (
          <button
            type="button" className="btn btn-ghost btn-sm"
            onClick={handleDiscardChanges}
            disabled={records.busy || deployState !== 'deployed'}
            title="Drop all pending changes and reload from the server"
            style={{ color: '#b91c1c' }}
          ><Trash2 size={14} /> Discard changes</button>
        ) : (
          <button
            type="button" className="btn btn-ghost btn-sm"
            onClick={handleDiscardChanges}
            disabled={records.busy || deployState !== 'deployed'}
            title="Undo all your edits and put every unsaved row back the way it was when this opened"
            style={{ color: '#b91c1c' }}
          ><RotateCcw size={14} /> Undo my edits</button>
        ))}
        {/* List-parity affordances (e.g. the Approvals inbox) sit just before
            the primary Add control. */}
        {beforeAddActions}
        {canAdd && (
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={handleAdd}
          disabled={records.busy || deployState !== 'deployed'}
        ><FilePlus size={14} /> Add row</button>
        )}
        {(canAdd || canEdit) && (
        <button
          type="button" className="btn btn-primary btn-sm"
          onClick={handleSaveAll}
          disabled={
            records.busy ||
            deployState !== 'deployed' ||
            (records.counts.unsaved + records.counts.dirty +
             records.counts.failed + records.counts.partial) === 0
          }
        >
          <Save size={14} /> Save All
          {(records.counts.unsaved + records.counts.dirty +
            records.counts.failed + records.counts.partial) > 0 && (
            <span style={{ marginLeft: 4, opacity: 0.85 }}>
              ({records.counts.unsaved + records.counts.dirty +
                records.counts.failed + records.counts.partial})
            </span>
          )}
        </button>
        )}
        {/* The 3-dot overflow menu (Deployment status / Freeze column / Reset
            data) — mirrors the list header so the spreadsheet has the same
            controls. Rendered after "Save All". */}
        {headerActions}
      </div>

      {/* A review host (the file import) says this in its own header. */}
      {records.hasPendingChanges && !(review && !autoRetrieve) && (
        <div style={{
          ...(screenChrome ? { margin: '10px 20px 0' } : {}),
          padding: '6px 10px', fontSize: '0.9125rem',
          background: 'rgba(234,179,8,0.10)',
          border: '1px solid rgba(234,179,8,0.40)',
          borderRadius: 6, color: '#a16207',
        }}>
          {autoRetrieve ? (
            <>
              Local changes are not synced with the server. Refresh is
              disabled until you <strong>Save All</strong> or{' '}
              <strong>Discard changes</strong>.
            </>
          ) : (
            <>
              Nothing here is saved yet. Review the rows, then click{' '}
              <strong>Save All</strong>.
            </>
          )}
        </div>
      )}

      {records.lastSaveError && (
        <div style={{
          padding: '6px 10px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 6, color: '#b91c1c', fontSize: '0.9125rem',
        }}>{records.lastSaveError}</div>
      )}

      <div
        ref={attachGrid}
        onKeyDown={onGridKeyDown}
        style={{
          position: 'relative',
          ...(screenChrome
            ? { marginTop: records.hasPendingChanges || records.lastSaveError ? 10 : 0 }
            : { border: '1px solid var(--color-border)', borderRadius: 8 }),
          background: 'var(--color-bg-secondary, #ffffff)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          // Grow to fill the available height so the inner scroll viewport
          // (not the page) owns the vertical scroll — that's what lets the
          // grid's sticky column header stick while rows scroll, and uses the
          // full height instead of leaving dead space below the table.
          flex: 1,
          minHeight: 0,
        }}
      >
        {!screenChrome && <EnforcedFilterControls
          model={sm}
          enforcedFilters={records.enforcedFilters}
          filterSets={records.filterSets}
          activeFilterSetId={records.activeFilterSetId}
          bypassed={records.bypassed}
          busy={records.busy || deployState !== 'deployed'}
          notices={records.filterNotices}
          project={project}
          objName={objName}
          onApplyFilters={onApplyOverrides}
          onSelectFilterSet={onSelectFilterSet}
        />}
        {!screenChrome && <GridToolbar
          ux={gridUx}
          search={grid.search}
          onSearchChange={grid.setSearch}
          sort={grid.sort}
          onClearSort={grid.clearSort}
          isFullScreen={grid.isFullScreen}
          onToggleFullScreen={grid.toggleFullScreen}
          addFilterButton={gridUx.filter !== false && (
            <AddFilterButton
              columns={cols}
              columnKeyOf={columnFormKey}
              filters={grid.filters}
              onUpsert={grid.upsertFilter}
              onRemove={grid.removeFilter}
            />
          )}
          rightSlot={
            <>
              {/* The status chips already count the rows (All N); only a
                  search / column filter narrowing them is said here. */}
              {visibleRecords.length !== records.records.length && (
                <span style={{
                  fontSize: '0.875rem', color: 'var(--color-text-secondary, #465062)',
                  whiteSpace: 'nowrap',
                }}>
                  {`${visibleRecords.length} of ${records.records.length} match`}
                </span>
              )}
              {viewSwitcher}
            </>
          }
        />}
        {gridUx.filter !== false && !screenChrome && (
          <FilterChipBar
            filters={grid.filters}
            columns={cols}
            columnKeyOf={columnFormKey}
            onRemove={grid.removeFilter}
            onClearAll={grid.clearFilters}
          />
        )}
        {selectEnabled && selectedRowIds.size > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '8px 16px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-surface-2, var(--color-bg-secondary))',
          }}>
            <span style={{ fontSize: '0.9375rem', whiteSpace: 'nowrap' }}>
              <strong>{selectedRowIds.size}</strong> selected
            </span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
              {(bulkActions || []).map((a) => (
                <button
                  key={`bulk-${a.name}`}
                  className="btn btn-primary btn-sm"
                  disabled={bulkBusy}
                  onClick={() => runBulkActionGuarded(a.name)}
                  title={`Run "${a.name}" on the ${selectedRowIds.size} selected record${selectedRowIds.size === 1 ? '' : 's'}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: bulkBusy ? 'wait' : 'pointer' }}
                >
                  <Play size={13} /> {a.name}
                </button>
              ))}
              {review?.selectable && (
                <>
                  <button
                    type="button" className="btn btn-ghost btn-sm"
                    onClick={() => { dropRecords([...selectedRowIds]); clearSelection() }}
                    disabled={records.busy}
                    title="Drop the selected records from this edit set — nothing is deleted, and Undo brings them back"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <Trash2 size={13} /> {review.removeLabel || 'Remove from edit set'}
                  </button>
                  {details.length > 0 && (
                    <button
                      type="button" className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setExpandedMap(prev => {
                          const next = new Map(prev)
                          selectedRowIds.forEach(id => next.set(id, true))
                          return next
                        })
                        setScrollOnExpandId(null)
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <ChevronRight size={13} /> Open details
                    </button>
                  )}
                </>
              )}
            </div>
            <button
              className="btn btn-ghost btn-sm"
              onClick={clearSelection}
              disabled={bulkBusy}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <X size={13} /> Clear
            </button>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div
          ref={(node) => { viewportNodeRef.current = node; scrollViewportRef(node) }}
          style={{
            flex: 1, minHeight: 0, minWidth: 0,
            display: 'flex', flexDirection: 'column',
            // SplitScrollTable owns the scroll panes; this wrapper only sizes it.
            overflow: 'hidden',
            fontSize: dense ? '0.8rem' : undefined,
          }}
        >
          <TabularGrid
            project={project}
            objName={objName}
            splitHandle={splitHandle}
            sm={sm}
            details={details}
            childGrid={childGrid}
            gridLayout={gridLayout}
            mainFormNo={mainFormNo}
            panelWidth={viewportWidth}
            canAdd={canAdd}
            records={visibleRecords}
            renderLimit={renderLimit}
            onRenderMore={renderAtLeast}
            sort={grid.sort}
            onToggleSort={gridUx.sort !== false ? grid.toggleSort : undefined}
            selectedId={selectedId}
            isExpanded={isExpandedRec}
            anyExpanded={anyExpanded}
            onSetAllExpanded={setAllExpanded}
            scrollOnExpandId={scrollOnExpandId}
            trail={trail}
            dense={dense}
            seeded={seeded}
            youChanged={youChanged}
            removeLabel={review?.removeLabel}
            rowMenuItems={review?.rowMenuItems}
            onDuplicate={canAdd ? handleDuplicate : undefined}
            busy={records.busy || deployState !== 'deployed'}
            onSelect={handleSelect}
            onToggleExpand={toggleExpand}
            onCellChange={onCellChange}
            onRemove={handleRemove}
            columnTotals={showTotals ? columnTotals : null}
            uploadDocument={api.makeUploader(dataAccess, project)}
            rowActions={hasRowActions ? rowActions : undefined}
            onRunRowAction={runRowActionGuarded}
            bulkEnabled={selectEnabled}
            selectedRowIds={selectedRowIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            allSelected={allSelected}
            onToggleSelectAll={toggleSelectAll}
          />
        </div>
        {hostPanel}
        </div>
        <ErrorPanel
          open={errorPanelOpen && !hostPanel}
          errors={errorItems}
          onClose={() => setErrorPanelOpen(false)}
          onSelect={handleErrorSelect}
        />
      </div>
      {/* "Ignore warnings and proceed" confirm for save warnings. */}
      {warningsModal}
      {/* Unsaved-changes gate shown before a custom/bulk action runs. */}
      {saveDiscardModal}
    </div>
    </TxnRuntimeProvider>
  )
}

// ── Inline grid ─────────────────────────────────────────────

function TabularGrid({
  project, objName, sm, details, childGrid, gridLayout, mainFormNo, panelWidth, records, sort, onToggleSort,
  renderLimit, onRenderMore,
  selectedId, isExpanded, anyExpanded, onSetAllExpanded, scrollOnExpandId,
  trail, dense, seeded, youChanged, removeLabel, rowMenuItems, onDuplicate,
  busy, canAdd,
  onSelect, onToggleExpand, onCellChange, onRemove, columnTotals, uploadDocument,
  rowActions, onRunRowAction, bulkEnabled, selectedRowIds, onRowCheckboxClick,
  allSelected, onToggleSelectAll, splitHandle,
}: {
  project: string
  objName: string
  splitHandle?: React.Ref<SplitScrollHandle>
  sm: SqlModel
  details: Array<{ formNo: string; sm: SqlModel }>
  childGrid: ChildGridApi
  gridLayout: GridLayoutApi
  mainFormNo: string
  /** Gate the empty-state's "click + Add row" hint (read-only viewers can't). */
  canAdd?: boolean
  /** Visible width of the scroll viewport, for pinning the expanded
   *  child-form panel so it doesn't span the full parent-table width. */
  panelWidth: number
  records: EmbedRecord[]
  /** Draw at most this many rows (more load as the user scrolls). */
  renderLimit: number
  /** Ask for at least `n` rows to be drawn. Idempotent. */
  onRenderMore: (n: number) => void
  sort: { column: string; dir: 'asc' | 'desc' }[]
  onToggleSort?: (column: string, additive: boolean) => void
  selectedId: string | null
  isExpanded: (rec: EmbedRecord) => boolean
  anyExpanded: boolean
  onSetAllExpanded: (open: boolean) => void
  /** The record whose chevron was just opened — it scrolls itself to the top. */
  scrollOnExpandId: string | null
  trail: ChangeTrail
  dense: boolean
  seeded: ReadonlyMap<string, EmbedRecord> | null
  youChanged: (rec: EmbedRecord) => boolean
  removeLabel?: string
  rowMenuItems?: (rec: EmbedRecord) => RowAction[]
  onDuplicate?: (id: string) => void
  busy: boolean
  onSelect: (rec: EmbedRecord) => void
  onToggleExpand: (rec: EmbedRecord) => void
  onCellChange: (id: string, dbName: string, v: unknown) => void
  onRemove: (id: string) => void
  columnTotals: Record<string, EmbedColumnTotal> | null
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  // Custom row actions (3-dot) + multi-row bulk selection.
  rowActions?: ActionDef[]
  onRunRowAction?: (name: string, rec: EmbedRecord) => void
  bulkEnabled?: boolean
  selectedRowIds: Set<string>
  onRowCheckboxClick: (e: React.MouseEvent, id: string) => void
  allSelected: boolean
  onToggleSelectAll: () => void
}) {
  const hasDetails = details.length > 0
  // Apply the live width/order override (if any) on top of the designer's
  // column metadata. Hooks run before the empty-records early return below.
  const layout = gridLayout.layouts[mainFormNo]
  const baseCols = useMemo(() => gridOrderedColumns(sm), [sm])
  const cols = useMemo(() => orderedDisplayCols(baseCols, layout), [baseCols, layout])
  const wOf = useCallback((c: ColumnDef) => widthOf(c, layout), [layout])
  const displayKeys = useMemo(() => cols.map(colKeyOf), [cols])
  const dnd = useColumnDnd({
    displayKeys,
    enabled: true,
    onReorder: (keys) => gridLayout.setOrder(mainFormNo, keys),
    onResize: (k, w) => gridLayout.setWidth(mainFormNo, k, w),
  })

  if (records.length === 0) {
    return (
      <div style={{
        padding: '24px 16px', textAlign: 'center',
        color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
      }}>
        {busy
          ? 'Loading…'
          : canAdd
            ? 'No records match. Adjust filters / search, or click + Add row.'
            : 'No records match. Adjust filters / search.'}
      </div>
    )
  }

  const sortIndex = new Map<string, { dir: 'asc' | 'desc'; idx: number }>()
  sort.forEach((s, i) => sortIndex.set(s.column, { dir: s.dir, idx: i }))

  // Page the DOM, never the data: always draw far enough to include the
  // selected and the expanded row, so jumping to an error or a search hit
  // lands on a row that exists.
  let mustDraw = 0
  if (selectedId || scrollOnExpandId) {
    for (let i = records.length - 1; i >= 0; i--) {
      const id = records[i].id
      if (id === selectedId || id === scrollOnExpandId) { mustDraw = i + 1; break }
    }
  }
  const drawCount = Math.min(records.length, Math.max(renderLimit, mustDraw))
  const drawn = drawCount < records.length ? records.slice(0, drawCount) : records
  const remaining = records.length - drawCount

  return (
    <SplitScrollTable
      ref={splitHandle}
      storageKey={`twasta:split:sheet:${cols[0]?.table_name || sm.form_no || objName}`}
      dataCount={cols.length}
    >
      {(range) => {
        const paneCols = cols.slice(range.colStart, range.colEnd)
        const leadGutter = range.isLeading
        const tailGutter = range.isTrailing
        const paneTableWidth = paneCols.reduce(
          (sum, c) => sum + wOf(c),
          (leadGutter
            ? STATUS_COL_WIDTH + LINENO_COL_WIDTH + (hasDetails ? EXPAND_COL_WIDTH : 0)
              + (bulkEnabled ? SELECT_COL_WIDTH : 0)
            : 0)
            + (tailGutter ? ACTIONS_COL_WIDTH : 0),
        )
        const paneTotalCols = paneCols.length
          + (leadGutter ? 2 + (hasDetails ? 1 : 0) + (bulkEnabled ? 1 : 0) : 0)
          + (tailGutter ? 1 : 0)
        return (
    <table style={{
      borderCollapse: 'collapse', fontSize: '0.9375rem',
      tableLayout: 'fixed', width: paneTableWidth,
    }}>
      <colgroup>
        {leadGutter && bulkEnabled && <col style={{ width: SELECT_COL_WIDTH }} />}
        {leadGutter && <col style={{ width: STATUS_COL_WIDTH }} />}
        {leadGutter && <col style={{ width: LINENO_COL_WIDTH }} />}
        {leadGutter && hasDetails && <col style={{ width: EXPAND_COL_WIDTH }} />}
        {paneCols.map((c) => (
          <col
            key={c.col_id || columnFormKey(c)}
            data-col-key={colKeyOf(c)}
            style={{ width: wOf(c) }}
          />
        ))}
        {tailGutter && <col style={{ width: ACTIONS_COL_WIDTH }} />}
      </colgroup>
      <thead>
        <tr>
          {leadGutter && bulkEnabled && (
            <th style={{ ...thStyle, textAlign: 'center' }}>
              <input
                type="checkbox"
                aria-label="Select all rows"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = !allSelected && selectedRowIds.size > 0 }}
                onChange={onToggleSelectAll}
                style={{ cursor: 'pointer' }}
              />
            </th>
          )}
          {leadGutter && <th style={thStyle} title="What saving would do to this row">Change</th>}
          {leadGutter && <th style={thStyle}>#</th>}
          {leadGutter && hasDetails && (
            <th style={{ ...thStyle, padding: 0, textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => onSetAllExpanded(!anyExpanded)}
                aria-label={anyExpanded ? 'Collapse all details' : 'Expand all details'}
                title={anyExpanded ? 'Collapse all details' : 'Expand all details'}
                style={chevronBtnStyle(anyExpanded)}
              ><ChevronRight size={13} strokeWidth={2.6} /></button>
            </th>
          )}
          {paneCols.map((c, i) => {
            const key = columnFormKey(c)
            const ck = colKeyOf(c)
            const ss = sortIndex.get(key)
            const sortable = !!onToggleSort
            const ind = dnd.dropIndicator?.key === ck ? dnd.dropIndicator.side : null
            return (
              <th
                key={c.col_id || key}
                data-split-col={range.colStart + i}
                {...dnd.getHeaderProps(ck)}
                style={{
                  ...thStyle,
                  cursor: 'grab',
                  userSelect: 'none',
                  width: wOf(c),
                  boxShadow: ind === 'left'
                    ? 'inset 3px 0 0 var(--color-accent)'
                    : ind === 'right'
                      ? 'inset -3px 0 0 var(--color-accent)'
                      : undefined,
                }}
                onClick={sortable ? (e) => onToggleSort!(key, e.shiftKey) : undefined}
                title={sortable
                  ? 'Drag to reorder · drag the right edge to resize · click to sort (shift-click adds a column)'
                  : 'Drag to reorder · drag the right edge to resize'}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  {listHeadingOf(c) ?? key}
                  {isMandatory(c) && (
                    <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
                  )}
                  {ss && (
                    <span style={{
                      color: 'var(--color-accent, #6366f1)',
                      fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center',
                    }}>
                      {ss.dir === 'asc' ? <ArrowUpAZ size={11} /> : <ArrowDownAZ size={11} />}
                      {sort.length > 1 && (
                        <sup style={{ marginLeft: 2 }}>{ss.idx + 1}</sup>
                      )}
                    </span>
                  )}
                </span>
                <div {...dnd.getResizeHandleProps(ck)} style={RESIZE_HANDLE_STYLE} />
              </th>
            )
          })}
          {tailGutter && <th style={{ ...thStyle, textAlign: 'right' }}></th>}
        </tr>
      </thead>
      <tbody>
        {drawn.map((rec, i) => {
          const isSel = rec.id === selectedId
          const open = hasDetails && isExpanded(rec)
          return (
            <FragmentRow
              key={rec.id}
              range={range}
              project={project}
              objName={objName}
              rec={rec}
              cols={paneCols}
              details={details}
              parentSm={sm}
              childGrid={childGrid}
              gridLayout={gridLayout}
              panelWidth={panelWidth}
              lineNo={i + 1}
              isSel={isSel}
              isExpanded={open}
              scrollOnExpand={open && scrollOnExpandId === rec.id}
              trail={trail}
              dense={dense}
              seed={seeded?.get(rec.id)}
              youChanged={youChanged(rec)}
              removeLabel={removeLabel}
              rowMenuItems={rowMenuItems}
              onDuplicate={onDuplicate}
              hasDetails={hasDetails}
              totalCols={paneTotalCols}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onCellChange={onCellChange}
              onRemove={onRemove}
              uploadDocument={uploadDocument}
              rowActions={rowActions}
              onRunRowAction={onRunRowAction}
              bulkEnabled={bulkEnabled}
              isChecked={selectedRowIds.has(rec.id)}
              onCheckboxClick={onRowCheckboxClick}
            />
          )
        })}
        {remaining > 0 && (
          // One per split pane so the panes' rows stay the same height; only
          // the leading pane carries the label and the button.
          <LoadMoreRow
            key={drawCount}
            colSpan={paneTotalCols}
            remaining={remaining}
            showLabel={leadGutter}
            onMore={() => onRenderMore(drawCount + RENDER_PAGE)}
          />
        )}
      </tbody>
      {columnTotals && (
        <tfoot>
          <ColumnTotalsRow
            columns={paneCols}
            columnKeyOf={columnFormKey}
            columnWidthOf={wOf}
            rows={records.map(r => r.header)}
            totals={columnTotals}
            leadingCells={leadGutter ? (
              <>
                {bulkEnabled && (
                  <td style={{ ...tdStyle, background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                )}
                <td style={{ ...tdStyle, background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                <td style={{ ...tdStyle, background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                {hasDetails && (
                  <td style={{ ...tdStyle, background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                )}
              </>
            ) : undefined}
            trailingCell={tailGutter ? (
              <td style={{ ...tdStyle, background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
            ) : undefined}
          />
        </tfoot>
      )}
    </table>
        )
      }}
    </SplitScrollTable>
  )
}

/** The row after the last drawn one: draws the next page when it scrolls
 *  near view, with a button for keyboard users / no IntersectionObserver.
 *  Keyed by the drawn count, so it re-observes after every page — a short
 *  page that leaves it still in view loads the next one straight away. */
function LoadMoreRow({ colSpan, remaining, showLabel, onMore }: {
  colSpan: number
  remaining: number
  showLabel: boolean
  onMore: () => void
}) {
  const ref = useRef<HTMLTableRowElement>(null)
  const onMoreRef = useRef(onMore)
  onMoreRef.current = onMore
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    let io: IntersectionObserver | null = null
    try {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) onMoreRef.current()
      }, { rootMargin: '400px 0px' })
      io.observe(el)
    } catch (err) {
      console.warn('[EmbedCaptureTabularPreview] scroll paging unavailable; use the button', err)
    }
    return () => io?.disconnect()
  }, [])
  return (
    <tr ref={ref}>
      <td colSpan={colSpan} style={{ ...tdStyle, height: 36, textAlign: 'center' }}>
        {showLabel && (
          <button
            type="button" className="btn btn-ghost btn-sm"
            onClick={onMore}
            style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}
          >
            Show {Math.min(remaining, RENDER_PAGE)} more of {remaining} remaining row{remaining === 1 ? '' : 's'}
          </button>
        )}
      </td>
    </tr>
  )
}

function FragmentRow({
  range, project, objName, rec, cols, details, parentSm, childGrid, gridLayout, panelWidth, lineNo, isSel, isExpanded,
  scrollOnExpand, trail, dense, seed, youChanged, removeLabel, rowMenuItems, onDuplicate,
  hasDetails, totalCols,
  onSelect, onToggleExpand, onCellChange, onRemove, uploadDocument,
  rowActions, onRunRowAction, bulkEnabled, isChecked, onCheckboxClick,
}: {
  range: SplitRange
  project: string
  objName: string
  rec: EmbedRecord
  cols: ColumnDef[]
  details: Array<{ formNo: string; sm: SqlModel }>
  parentSm: SqlModel
  childGrid: ChildGridApi
  gridLayout: GridLayoutApi
  panelWidth: number
  lineNo: number
  isSel: boolean
  isExpanded: boolean
  scrollOnExpand: boolean
  trail: ChangeTrail
  dense: boolean
  seed?: EmbedRecord
  youChanged: boolean
  removeLabel?: string
  rowMenuItems?: (rec: EmbedRecord) => RowAction[]
  onDuplicate?: (id: string) => void
  hasDetails: boolean
  totalCols: number
  onSelect: (rec: EmbedRecord) => void
  onToggleExpand: (rec: EmbedRecord) => void
  onCellChange: (id: string, dbName: string, v: unknown) => void
  onRemove: (id: string) => void
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  rowActions?: ActionDef[]
  onRunRowAction?: (name: string, rec: EmbedRecord) => void
  bulkEnabled?: boolean
  isChecked?: boolean
  onCheckboxClick?: (e: React.MouseEvent, id: string) => void
}) {
  const rowBg = isSel ? 'rgba(99,102,241,0.10)' : undefined
  const fix = needsFix(rec)
  const tdPad: React.CSSProperties = dense ? { ...tdStyle, padding: '2px 8px' } : tdStyle

  // Lazy-load a saved parent's existing child rows the first time its row is
  // expanded (or after a save clears the cache). Rows land in the parent
  // record's `details[table]` so Save-All commits them; an unsaved parent
  // (no PK) starts with an empty, immediately-editable child grid.
  useEffect(() => {
    // Child rows live only in the right (trailing) pane, so only that instance
    // lazy-loads them — the left pane renders a height-matched spacer.
    if (!isExpanded || !rec.primaryKey || !range.isTrailing) return
    const ppk = buildParentPk(parentSm, rec.header)
    if (Object.keys(ppk).length === 0) return
    for (const d of details) {
      // Key child rows by the child form_no — always present + unique, unlike
      // main_table which some models leave empty.
      if (rec.details[d.formNo] !== undefined) continue
      childGrid.loadChildRows(d.formNo, ppk)
        .then(rows => childGrid.seedDetails(rec.id, d.formNo, rows))
        .catch(() => childGrid.seedDetails(rec.id, d.formNo, []))
    }
    // Re-run on expand, identity change, and after a save (status/PK change);
    // the per-table `details[table] !== undefined` guard prevents reload loops
    // and never clobbers in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, rec.id, rec.primaryKey, rec.status])

  // Single-row accordion: expanding a row collapses any other, which shifts
  // this row's position. Once expanded (after layout settles), scroll this row
  // to the top of the scroll viewport so the user lands on its child form
  // instead of having to scroll back up to it.
  const rowRef = useRef<HTMLTableRowElement | null>(null)
  useEffect(() => {
    // Scroll the master (right) pane; the left pane follows via scroll-sync.
    // Only the row whose chevron was clicked — expand-all and "open details
    // of changed records" open many rows and must leave the viewport alone.
    if (!isExpanded || !scrollOnExpand || !range.isTrailing) return
    rowRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [isExpanded, scrollOnExpand, range.isTrailing])

  return (
    <>
      <tr
        ref={rowRef}
        data-split-row={rec.id}
        onClick={() => onSelect(rec)}
        style={{
          background: rowBg,
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {range.isLeading && bulkEnabled && (
          <td style={{ ...tdStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              aria-label="Select row"
              checked={!!isChecked}
              onChange={() => {}}
              onClick={(e) => onCheckboxClick?.(e, rec.id)}
              style={{ cursor: 'pointer' }}
            />
          </td>
        )}
        {range.isLeading && (
          <td style={{ ...tdPad, padding: '0 6px', boxShadow: fix ? 'inset 3px 0 0 #d92d20' : undefined }}>
            <StatusTag rec={rec} fix={fix} youChanged={youChanged} />
          </td>
        )}
        {range.isLeading && <td style={{ ...tdPad, color: 'var(--color-text-secondary)' }}>{lineNo}</td>}
        {range.isLeading && hasDetails && (
          <td style={{ ...tdPad, padding: 0, textAlign: 'center' }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleExpand(rec) }}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
              title={isExpanded ? 'Collapse details' : 'Expand details'}
              style={chevronBtnStyle(isExpanded)}
            ><ChevronRight size={13} strokeWidth={2.6} /></button>
          </td>
        )}
        {cols.map((c) => {
          const db = columnFormKey(c)
          const err = rec.fieldErrors[db]
          const change = trail === 'off' || err ? null : cellChange(rec, seed, db)
          const tone = change ? TRAIL_TONE[change.by] : null
          return (
            // Note: no stopPropagation here — letting the cell click
            // bubble to the row's onClick is what writes the selection
            // variables. The cell's input still receives focus.
            <td
              key={c.col_id || db}
              style={{
                ...tdCellStyle,
                ...(dense ? { padding: '1px 4px' } : null),
                ...(tone ? { background: tone.bg } : null),
              }}
              data-grid-cell={`${rec.id}::${db}`}
              data-grid-row={rec.id}
              data-grid-col={db}
            >
              {change && trail === 'markers' && <ChangedCellMarker original={change.old} by={change.by} />}
              {change && trail === 'inline' && (
                <div
                  title={`${TRAIL_TONE[change.by].was}: ${formatOriginal(change.old)}`}
                  style={{
                    fontSize: 13, lineHeight: 1.2, padding: '1px 6px 0',
                    color: TRAIL_TONE[change.by].fg, textDecoration: 'line-through',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >{formatOriginal(change.old)}</div>
              )}
              <InlineCellEditor
                project={project}
                objName={objName}
                col={c}
                value={rec.header[db]}
                error={rec.fieldErrors[db]}
                readOnlyKey={!!rec.primaryKey}
                viewOnly={!rowEditable(rec.header)}
                onChange={(v) => onCellChange(rec.id, db, v)}
                rowValues={rec.header}
                uploadDocument={uploadDocument}
              />
            </td>
          )
        })}
        {range.isTrailing && (
        <td
          style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}
          onClick={(e) => e.stopPropagation()}
        >
          <RowActionsMenu
            actions={[
              // Host-supplied items first (e.g. the import's "Show in source").
              ...(rowMenuItems?.(rec) || []),
              // Row-scoped custom actions run against THIS record (the grid
              // flushes unsaved edits first, then resolves its txn_id), minus
              // any the engine hid for it (visible_when). A row still being
              // typed has never been through a list, so it carries no
              // ``_hidden_actions`` and keeps the full menu.
              ...visibleRowActions(rowActions, rec.hiddenActions).map((a) => ({
                label: a.name,
                icon: <Play size={14} />,
                onClick: () => onRunRowAction?.(a.name, rec),
              })),
              ...(onDuplicate ? [{
                label: 'Duplicate row',
                icon: <Copy size={14} />,
                onClick: () => onDuplicate(rec.id),
              }] : []),
              {
                label: removeLabel || 'Remove row',
                icon: <Trash2 size={14} />,
                onClick: () => onRemove(rec.id),
                danger: true,
              },
            ]}
          />
        </td>
        )}
      </tr>
      {rec.recordError && (
        <tr data-split-row={`${rec.id}::err`}>
          <td colSpan={totalCols} style={{
            padding: '4px 12px',
            background: 'rgba(239,68,68,0.06)',
            color: '#b91c1c', fontSize: '0.8125rem',
            borderBottom: '1px solid rgba(239,68,68,0.25)',
          }}>{range.isTrailing ? rec.recordError : ''}</td>
        </tr>
      )}
      {/* Detail child grid renders only in the right (trailing) pane; the left
          pane emits an empty, height-matched spacer row so both panes stay
          vertically aligned (heights paired by data-split-row). */}
      {isExpanded && hasDetails && !range.isTrailing && (
        <tr data-split-row={`${rec.id}::detail`}>
          <td colSpan={totalCols} style={{ padding: 0, background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
        </tr>
      )}
      {isExpanded && hasDetails && range.isTrailing && (
        <tr data-split-row={`${rec.id}::detail`}>
          <td colSpan={totalCols} style={{
            padding: 0,
            background: 'var(--color-surface-2, var(--color-bg-secondary))',
          }}>
            {/* Pin the panel to the visible viewport width (sticky/left:0)
                so it doesn't inherit the full wide parent-table width — the
                child grid's Add/Refresh toolbar then stays on-screen and the
                child table scrolls inside its own bounds. */}
            <div style={{
              // Pin to the visible width of the SCROLL PANE this row lives in
              // (the split-table publishes it as --ss-viewport) so the child
              // grid's toolbar stays on-screen and doesn't run past the pane —
              // falling back to the measured viewport width when unsplit.
              position: 'sticky',
              left: 0,
              width: `var(--ss-viewport, ${panelWidth ? `${panelWidth}px` : 'auto'})`,
              maxWidth: `var(--ss-viewport, ${panelWidth ? `${panelWidth}px` : 'none'})`,
              boxSizing: 'border-box',
              padding: '8px 12px 12px',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {details.map((d, di) => (
                <div key={d.formNo || di} style={{
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  background: 'var(--color-bg)', overflow: 'hidden',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '4px 10px', fontSize: '0.8125rem',
                    background: 'var(--color-surface-2, var(--color-bg-secondary))',
                    borderBottom: '1px solid var(--color-border)',
                  }}>
                    <span style={{ fontWeight: 600 }}>
                      {d.sm.form_title || `Detail ${di + 1}`}
                    </span>
                    <span style={{
                      padding: '1px 6px', borderRadius: 8, fontSize: '0.8125rem',
                      background: 'var(--color-accent-weak, rgba(76,141,255,0.16))', color: 'var(--color-accent)',
                    }}>Detail grid</span>
                  </div>
                  {/* Inline multi-tabular child grid — same cell editors,
                      designer widths and Excel paste/fill as the main form.
                      Edits write to this parent record's details[table]; a
                      DEFINITE height + overflow:hidden keeps the toolbar pinned
                      while only the table body scrolls. */}
                  <div style={{ height: 320, overflow: 'hidden' }}>
                    <ChildTabularGrid
                      execute={childGrid.execute}
                      project={project}
                      objName={objName}
                      formNo={d.formNo}
                      childSm={d.sm}
                      rows={(rec.details[d.formNo] as ChildRow[]) || []}
                      onMutate={(updater, coalesceKey) =>
                        childGrid.updateDetailRows(rec.id, d.formNo, updater, coalesceKey)}
                      parentHeaderValues={rec.header}
                      uploadDocument={childGrid.uploadDocument}
                      dataAccess={childGrid.dataAccess}
                      busy={childGrid.busy}
                      parentStatus={rec.status}
                      gridLayout={gridLayout}
                      model={childGrid.model}
                      recordId={rec.id}
                      recordTxnId={rec.primaryKey || undefined}
                      loadChildRows={childGrid.loadChildRows}
                      seedRowChildren={childGrid.seedRowChildren}
                      depth={1}
                      readOnly={formLinesViewOnly(d.sm)}
                      addLabel={d.sm.add_label}
                      onAncestorPatch={(forms) => {
                        const headerNo = childGrid.model
                          ? (allSqlModels(childGrid.model)[0]?.formNo ?? '1') : '1'
                        const set = forms[headerNo]?.set
                        if (set && Object.keys(set).length) childGrid.updateHeader(rec.id, set)
                      }}
                      registerCellSetter={(setter) =>
                        childGrid.registerCellSetter(rec.id, d.formNo, setter)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ── Styles ──────────────────────────────────────────────────

// Excel-like data grid: gridlines on all sides (the table uses borderCollapse
// so borders merge into single 1px lines), a header row that blends with the
// app theme, and cells that adapt to the theme — white-on-dark-text in the
// light theme, light-text-on-dark in the dark theme.
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 600,
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
  border: '1px solid var(--color-border)',
  position: 'sticky', top: 0, zIndex: 2,
  textTransform: 'none', letterSpacing: 'normal',
  // One line: a long heading is cut (full text on hover), never wrapped.
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}
const tdStyle: React.CSSProperties = {
  padding: '6px 10px', color: 'var(--color-text)',
  background: 'var(--lp-input-bg, var(--color-bg-secondary, #ffffff))',
  border: '1px solid var(--color-border)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  verticalAlign: 'middle',
}
/** Loose cell equality: '' ≡ null, 9 ≡ '9' ≡ '9.00', a date ≡ its midnight stamp. */
function sameCellValue(a: unknown, b: unknown): boolean {
  const sa = a == null ? '' : typeof a === 'object' ? JSON.stringify(a) : String(a).trim()
  const sb = b == null ? '' : typeof b === 'object' ? JSON.stringify(b) : String(b).trim()
  if (sa === sb) return true
  if (sa === '' || sb === '') return false
  if (!Number.isNaN(Number(sa)) && !Number.isNaN(Number(sb))) return Number(sa) === Number(sb)
  const day = (x: string) => /^(\d{4}-\d{2}-\d{2})(?:[ T]00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?)?$/.exec(x)?.[1]
  const da = day(sa)
  return !!da && da === day(sb)
}

/**
 * Who changed a cell, and what it held before — null when it is unchanged.
 *
 * With a SEED (a review screen, e.g. the file import) the seed is the
 * baseline: the cell differing from the seed is the user's change (was: the
 * seed's value); the seed differing from what is stored is the file's (was:
 * the stored value). Without one, a loaded record's cell that no longer
 * matches what is stored is the user's. A saved record has nothing pending.
 */
export function cellChange(
  rec: EmbedRecord, seed: EmbedRecord | undefined, key: string,
): { by: CellChangeBy; old: unknown } | null {
  if (rec.status === 'saved' || rec.status === 'retrieved') return null
  if (seed) {
    if (!sameCellValue(seed.header[key], rec.header[key])) return { by: 'you', old: seed.header[key] }
    if (rec.originalHeader && !sameCellValue(rec.originalHeader[key], seed.header[key])) {
      return { by: 'file', old: rec.originalHeader[key] }
    }
    return null
  }
  if (!rec.primaryKey || !rec.originalHeader) return null
  return sameCellValue(rec.originalHeader[key], rec.header[key])
    ? null : { by: 'you', old: rec.originalHeader[key] }
}

/** Any user-facing value differs between two header / row value maps.
 *  Underscore keys are the client's own bookkeeping, not values. */
function rowValuesDiffer(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})])
  for (const k of keys) {
    if (k.startsWith('_')) continue
    if (!sameCellValue(a?.[k], b?.[k])) return true
  }
  return false
}

/** Have one form's detail rows changed since the seed? A form the seed never
 *  held was lazy-loaded on expand, so only a row the user added, edited or
 *  deleted there counts — not the load itself. Nested rows recurse. */
export function detailRowsChanged(seedRows: ChildRow[] | undefined, rows: ChildRow[] | undefined): boolean {
  const cur = rows || []
  if (seedRows === undefined) return cur.some(r => !r._txn_id || !!r._edited || !!r._deleted)
  if (seedRows.length !== cur.length) return true
  for (let i = 0; i < cur.length; i++) {
    const a = seedRows[i]
    const b = cur[i]
    if (!!a._deleted !== !!b._deleted || rowValuesDiffer(a, b)) return true
    const ac = a.__children || {}
    const bc = b.__children || {}
    for (const f of new Set([...Object.keys(ac), ...Object.keys(bc)])) {
      if (detailRowsChanged(ac[f], bc[f])) return true
    }
  }
  return false
}

function localId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID()
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`
}

/** Detail rows copied as NEW rows (Duplicate row): values only — no server
 *  id, status flag or error — with pending deletes left behind. */
export function copyDetailRows(rows: ChildRow[]): ChildRow[] {
  return rows.filter(r => !r._deleted).map((r) => {
    const out: ChildRow = { _localId: localId() }
    for (const [k, v] of Object.entries(r)) if (!k.startsWith('_')) out[k] = v
    if (r.__children) {
      out.__children = Object.fromEntries(
        Object.entries(r.__children).map(([f, kids]) => [f, copyDetailRows(kids || [])]),
      )
    }
    return out
  })
}

function chevronBtnStyle(open: boolean): React.CSSProperties {
  return {
    width: 24, height: 24, border: 0, borderRadius: 5, padding: 0,
    background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transform: open ? 'rotate(90deg)' : undefined, transition: 'transform 0.12s ease',
  }
}

function formatOriginal(v: unknown): string {
  if (v == null || (typeof v === 'string' && v.trim() === '')) return '(empty)'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** Corner marker on a changed cell — amber for a change the file made, purple
 *  for one the user made; hovering it shows what the cell held before. */
function ChangedCellMarker({ original, by }: { original: unknown; by: CellChangeBy }) {
  const [open, setOpen] = useState(false)
  const text = formatOriginal(original)
  const tone = TRAIL_TONE[by]
  return (
    <span
      role="note"
      aria-label={`${tone.was}: ${text}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      style={{
        position: 'absolute', top: 0, right: 0, width: 12, height: 12, zIndex: 2,
        cursor: 'help',
      }}
    >
      <span style={{
        position: 'absolute', top: 0, right: 0, width: 0, height: 0,
        borderTop: `7px solid ${tone.mark}`,
        borderLeft: '7px solid transparent',
      }} />
      {open && (
        <span style={{
          position: 'absolute', top: 12, right: 0, zIndex: 30,
          minWidth: 120, maxWidth: 320, padding: '6px 8px', borderRadius: 6,
          background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
          border: '1px solid var(--color-border, #e5e7eb)',
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)', fontSize: 13, lineHeight: 1.4,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', pointerEvents: 'none',
        }}>
          <span style={{ display: 'block', fontSize: 13, color: tone.fg }}>
            {tone.was}
          </span>
          {text}
        </span>
      )}
    </span>
  )
}

const tdCellStyle: React.CSSProperties = {
  position: 'relative',
  padding: '4px 6px', color: 'var(--color-text)',
  background: 'var(--lp-input-bg, var(--color-bg))',
  border: '1px solid var(--color-border)',
  verticalAlign: 'middle',
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 14px',
  display: 'flex', alignItems: 'center', gap: 10,
  background: 'var(--color-bg)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
  color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
}
